import { useEffect, useMemo, useRef, useState } from "react";
import { Drawer, Modal, message } from "antd";
import { Link, useNavigate, useParams } from "react-router-dom";
import { CareShell, Panel } from "../../components/care/CareLayout";
import VehicleImage from "../../components/care/VehicleImage";
import Seo, { absoluteUrl } from "../../components/seo/Seo";
import useSiteProfile from "../../hooks/useSiteProfile";
import { careService } from "../../services/careService";
import { inventoryVehicles } from "./VehiclesListPage";

const fallbackSiteProfile = {
  name: "Auto Portal",
  phone: "",
  email: "",
  location: "",
  logo: "",
  logoUrl: "",
  favicon: "",
  faviconUrl: "",
};

const vehicleSections = [
  ["notes", "ti-notes", "Sales Notes", "Customer interest, deal updates, prep notes, and follow-up items.", "18 notes", "2024-05-24T09:15:00"],
  ["carePlan", "ti-speakerphone", "Merchandising Campaigns", "Listing copy, promotion notes, finance highlights, and sales positioning.", "In review", "2024-05-24T08:30:00"],
  ["documents", "ti-file-description", "Documents", "Title, registration, inspection, disclosure, and deal files.", "8 files", "2024-05-23T16:10:00"],
  ["alerts", "ti-alert-triangle", "Prep Alerts", "Open recon, inspection, pricing, and follow-up items.", "2 open", "2024-05-23T11:20:00"],
  ["appraisals", "ti-clipboard-text", "Appraisals", "Vehicle appraisal, pricing reviews, and inspection changes.", "2 pending", "2024-05-22T14:00:00"],
  ["team", "ti-users-group", "Sales Team", "Sales consultant, manager, inventory, finance, and service contacts.", "5 members", "2024-05-21T10:00:00"],
].sort((a, b) => new Date(b[5]).getTime() - new Date(a[5]).getTime());


const carePlanFlowStages = [
  {
    label: "State Initial Appraisal",
    existingLabel: "Yearly State Appraisal",
    text: "State appraisal received",
    existingText: "Annual state appraisal received",
    stageKeys: ["STATE_INITIAL_ASSESSMENT", "YEARLY_STATE_ASSESSMENT", "STATE_ASSESSMENT_RECEIVED"],
    icon: "ti-flag",
    appliesTo: ["new-vehicle", "existing-vehicle"],
  },
  {
    label: "MaG / Review",
    text: "Meet-and-Greet and provider decision",
    stageKeys: ["MEET_AND_GREET", "PROVIDER_REVIEW", "PROVIDER_ACCEPTED"],
    icon: "ti-clipboard-text",
    appliesTo: ["new-vehicle"],
  },
  {
    label: "Annual Review",
    text: "Review yearly notes and changes",
    stageKeys: ["ANNUAL_REVIEW", "YEARLY_REVIEW", "ANNUAL_ASSESSMENT"],
    icon: "ti-notes",
    appliesTo: ["existing-vehicle"],
  },
  {
    label: "Onboarding",
    text: "Signed paperwork and admission details",
    stageKeys: ["ADMISSION_AND_PAPERWORK", "CLIENT_ONBOARDING"],
    icon: "ti-clipboard-list",
    appliesTo: ["new-vehicle"],
  },
  {
    label: "30 Days Probation",
    text: "Observation period complete",
    stageKeys: ["THIRTY_DAY_PROBATION", "THIRTY_DAY_OBSERVATION", "NEW_RESIDENT_ASSESSMENT"],
    icon: "ti-notes",
    appliesTo: ["new-vehicle"],
  },
  {
    label: "Preliminary Plan",
    text: "Provider plan after 30-day notes",
    stageKeys: ["PRELIMINARY_CARE_PLAN"],
    icon: "ti-heart-handshake",
    appliesTo: ["new-vehicle"],
  },
  {
    label: "NCP",
    text: "Template, provider inputs, and pay negotiation",
    stageKeys: ["NEGOTIATED_CARE_PLAN", "NEGOTIATED_CARE_PLAN_DRAFT", "NCP_IN_PROGRESS", "PAYMENT_NEGOTIATION"],
    icon: "ti-file-pencil",
    appliesTo: ["new-vehicle", "existing-vehicle"],
  },
  {
    label: "Confirmed / Ongoing",
    text: "State confirmed files and ongoing daily notes",
    stageKeys: ["FINAL_ASSESSMENT_UPDATE", "STATE_CONFIRMATION", "ONGOING_CARE", "ACTIVE_CARE", "CONFIRMED", "CURRENT"],
    icon: "ti-circle-check",
    appliesTo: ["new-vehicle", "existing-vehicle"],
  },
];

const initialsFor = (name = "") => name.split(/\s+/).filter(Boolean).slice(0, 2).map((part) => part[0]).join("").toUpperCase() || "--";
const compactDate = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};
const numberFromText = (value) => {
  const numeric = Number(String(value || "").replace(/[^\d.]/g, ""));
  return Number.isFinite(numeric) ? numeric : 0;
};
const displayPrice = (value) => {
  const text = String(value || "").trim();
  if (!text) return "Price pending";
  if (/^\$/.test(text)) return text;
  const numeric = numberFromText(text);
  return numeric > 0
    ? numeric.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 })
    : text;
};
const displayMileage = (value) => {
  const text = String(value || "").trim();
  if (!text) return "Mileage pending";
  const numeric = numberFromText(text);
  return numeric > 0 ? `${numeric.toLocaleString("en-US")} miles` : text;
};
const scheduledVisitAtFromForm = (form = {}) => {
  if (form.scheduleVisit !== "yes" || !form.visitDate || !form.visitTime) return null;
  const date = new Date(`${form.visitDate}T${form.visitTime}:00`);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
};
const ageFromBirthDate = (birthDate) => {
  if (!birthDate) return "";
  const date = new Date(birthDate);
  if (Number.isNaN(date.getTime())) return "";
  const today = new Date();
  let age = today.getFullYear() - date.getFullYear();
  const hasBirthdayPassed =
    today.getMonth() > date.getMonth() ||
    (today.getMonth() === date.getMonth() && today.getDate() >= date.getDate());
  if (!hasBirthdayPassed) age -= 1;
  return age > -1 ? String(age) : "";
};
const fullNameFor = (vehicle = {}) =>
  vehicle.title ||
  vehicle.name ||
  vehicle.fullName ||
  vehicle.vehicleName ||
  [vehicle.firstName, vehicle.middleName, vehicle.lastName].filter(Boolean).join(" ") ||
  "Vehicle";
const locationFor = (vehicle = {}) => vehicle.locationInformation || vehicle.address || vehicle.location || {};
const contactFor = (vehicle = {}) => vehicle.contactInformation || vehicle.contact || {};
const addressSummaryFor = (vehicle = {}) => {
  const location = locationFor(vehicle);
  return [
    location.addressLine1 || location.address,
    location.addressLine2,
    location.city,
    location.county,
    location.state,
    location.country,
    location.zipCode || location.zip,
  ].filter(Boolean).join(", ");
};
const primaryPhoneFor = (vehicle = {}) => {
  const contact = contactFor(vehicle);
  return vehicle.primaryContactPhone || contact.primaryPhone || vehicle.phone || "";
};
const primaryEmailFor = (vehicle = {}) => {
  const contact = contactFor(vehicle);
  return vehicle.primaryContactEmail || contact.primaryEmail || vehicle.email || "";
};
const displayValue = (value, fallback = "Not set") => {
  if (Array.isArray(value)) return value.filter(Boolean).join(", ") || fallback;
  if (value === null || value === undefined || value === "") return fallback;
  return String(value);
};
const labelize = (value) => {
  const text = displayValue(value, "");
  return text ? text.replaceAll("_", " ").toLowerCase().replace(/\b\w/g, (letter) => letter.toUpperCase()) : "";
};
const normalizeStatusKey = (value) => String(value || "").trim().replace(/[\s-]+/g, "_").toUpperCase();
const hasHtml = (value) => /<\/?[a-z][\s\S]*>/i.test(String(value || ""));
const onboardingValue = (value) => {
  if (value === null || value === undefined || value === "") return "";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (Array.isArray(value)) return value.filter(Boolean).map(onboardingValue).join(", ");
  if (typeof value === "object") return Object.entries(value)
    .filter(([, entryValue]) => entryValue !== null && entryValue !== undefined && entryValue !== "")
    .map(([key, entryValue]) => `${labelize(key)}: ${onboardingValue(entryValue)}`)
    .join(" | ");
  return String(value);
};
const onboardingDocumentUrlFor = (document = {}) =>
  document.signedUrl ||
  document.downloadUrl ||
  document.previewUrl ||
  document.url ||
  document.fileUrl ||
  "";
const isImageOnboardingDocument = (document = {}) => {
  const contentType = String(document.contentType || document.mimeType || document.type || "").toLowerCase();
  const fileName = String(document.fileName || document.name || "").toLowerCase();
  return contentType.startsWith("image/") || /\.(png|jpe?g|gif|webp|bmp|svg)$/.test(fileName);
};
const onboardingDocumentMetaFor = (document = {}) =>
  [
    document.description,
    document.fileName,
    document.addedAt ? `Added ${compactDate(document.addedAt)}` : "",
    document.confirmed ? "Confirmed" : "",
    document.storageStatus,
  ].filter(Boolean).join(" | ");
const addressSummary = (address = {}) => [
  address.addressLine1 || address.address,
  address.addressLine2,
  address.city,
  address.county,
  address.state,
  address.country,
  address.zipCode || address.zip,
].filter(Boolean).join(", ");
const phonesSummary = (phones = {}) => [
  phones.preferred ? `Preferred: ${phones.preferred}` : "",
  phones.mobile ? `Mobile: ${phones.mobile}` : "",
  phones.office ? `Office: ${phones.office}` : "",
  phones.home ? `Home: ${phones.home}` : "",
].filter(Boolean).join(" | ");
const processDateValue = (process = {}) => {
  const value = process.updatedAt || process.createdAt || process.startDate || process.processStartDate || "";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? 0 : date.getTime();
};
const appraisalWorkflowStatus = (appraisal = {}) =>
  firstText(appraisal.workflowStatus, appraisal.processStatus, appraisal.status);
const closedAppraisalStatuses = ["COMPLETED", "COMPLETE", "CONFIRMED", "CLOSED", "CANCELLED", "CANCELED", "ARCHIVED", "DELETED"];
const isClosedAppraisalProcess = (process = {}) => {
  const status = normalizeStatusKey(appraisalWorkflowStatus(process));
  return closedAppraisalStatuses.includes(status);
};
const inventoryVehicleById = (vehicleId) => inventoryVehicles.find((item) => String(item.id) === String(vehicleId));
const silveradoListingDetails = {
  features: [
    ["Mileage", "41,905", "ti-dashboard"],
    ["Drivetrain", "Four-Wheel Drive", "ti-route"],
    ["Exterior color", "Summit White", "ti-droplet"],
    ["Interior color", "Jet Black", "ti-armchair"],
    ["MPG", "18 MPG", "ti-gas-station"],
    ["Engine", "355 hp 5.3L V8", "ti-engine"],
    ["Fuel type", "Gasoline", "ti-gas-station"],
    ["Transmission", "8-Speed Automatic", "ti-manual-gearbox"],
  ],
  overview: [
    ["Make", "Chevrolet"],
    ["Model", "Silverado 1500"],
    ["Year", "2021"],
    ["Trim", "LT"],
    ["Body type", "Truck / Crew Cab"],
    ["Exterior color", "Summit White"],
    ["Interior color", "Jet Black"],
    ["Mileage", "41,905 mi"],
    ["Condition", "Used"],
    ["VIN", "3GCUYDED1MG123456"],
    ["Stock number", "WE-SILV-LT"],
  ],
  fuelEconomy: [
    ["Fuel tank size", "24 gal"],
    ["Highway gas mileage", "21 MPG"],
    ["Combined gas mileage", "18 MPG"],
    ["Fuel type", "Gasoline"],
    ["City gas mileage", "16 MPG"],
  ],
  performance: [
    ["Transmission", "8-Speed Automatic"],
    ["Engine", "355 hp 5.3L V8"],
    ["Drivetrain", "Four-Wheel Drive"],
    ["Horsepower", "355 hp"],
  ],
  safetyRatings: [
    ["NHTSA overall safety rating", 4],
    ["NHTSA side crash rating", 5],
    ["NHTSA frontal crash rating", 4],
    ["NHTSA rollover rating", 4],
  ],
  safetyFeatures: [
    "ABS Brakes",
    "Backup Camera",
    "Stability Control",
    "Traction Control",
    "Driver Airbag",
    "Front Side Airbags",
    "Passenger Airbag",
  ],
  measurements: [
    ["Doors", "4 doors"],
    ["Back legroom", "43.4 in"],
    ["Front legroom", "44.5 in"],
    ["Cargo volume", "62.9 cu ft"],
  ],
  options: [
    "Appearance Package",
    "Bluetooth",
    "Alloy Wheels",
    "Tow Package",
    "Backup Camera",
    "Remote Start",
  ],
};

export const listingDetailsFor = (vehicle = {}) => {
  if (String(vehicle.id || vehicle.vehicleId || "").includes("silverado-lt")) return silveradoListingDetails;
  const titleParts = String(vehicle.title || vehicle.name || vehicle.vehicleName || "").split(" ").filter(Boolean);
  const year = vehicle.year || titleParts[0] || "";
  const make = vehicle.make || titleParts[1] || "";
  const model = vehicle.model || titleParts.slice(2).join(" ") || "";
  const mileage = vehicle.mileage || "";
  const drivetrain = vehicle.drivetrain || vehicle.careLevel || "";
  const fuel = vehicle.fuelType || vehicle.fuel || "";
  const exteriorColor = vehicle.exteriorColor || vehicle.color || "";
  const interiorColor = vehicle.interiorColor || "";
  const transmission = vehicle.transmission || "";
  const engine = vehicle.engine || "";
  const cityMpg = vehicle.mpgCity || vehicle.cityMpg || "";
  const highwayMpg = vehicle.mpgHighway || vehicle.highwayMpg || "";
  const mpg = vehicle.mpg || (cityMpg ? `${cityMpg} MPG` : "");

  return {
    features: [
      ["Mileage", String(mileage).replace(/\s*mi$/i, ""), "ti-dashboard"],
      ["Drivetrain", drivetrain, "ti-route"],
      ["Exterior color", exteriorColor, "ti-droplet"],
      ["Interior color", interiorColor, "ti-armchair"],
      ["MPG", mpg, "ti-gas-station"],
      ["Engine", engine, "ti-engine"],
      ["Fuel type", fuel, "ti-gas-station"],
      ["Transmission", transmission, "ti-manual-gearbox"],
    ],
    overview: [
      ["Make", make],
      ["Model", model],
      ["Year", year],
      ["Trim", vehicle.trim || ""],
      ["Body type", vehicle.bodyType || ""],
      ["Exterior color", exteriorColor],
      ["Interior color", interiorColor],
      ["Mileage", mileage],
      ["Condition", vehicle.condition || ""],
      ["VIN", vehicle.vin || ""],
      ["Stock number", vehicle.stockNumber || ""],
    ],
    fuelEconomy: [
      ["Fuel tank size", vehicle.fuelTankSize || ""],
      ["Highway gas mileage", highwayMpg ? `${highwayMpg} MPG` : ""],
      ["Fuel type", fuel],
      ["City gas mileage", cityMpg ? `${cityMpg} MPG` : ""],
    ],
    performance: [
      ["Transmission", transmission],
      ["Engine", engine],
      ["Drivetrain", drivetrain],
      ["Horsepower", vehicle.horsepower || ""],
    ],
    safetyRatings: vehicle.safetyRatings || [],
    safetyFeatures: vehicle.safetyFeatures || [],
    measurements: vehicle.measurements || [
      ["Doors", vehicle.doors || ""],
      ["Back legroom", vehicle.backLegroom || ""],
      ["Front legroom", vehicle.frontLegroom || ""],
      ["Cargo volume", vehicle.cargoVolume || ""],
    ],
    options: vehicle.options || [],
  };
};

const staticVehicleRecordFor = (vehicleId) => {
  const item = inventoryVehicleById(vehicleId);
  if (!item) return null;
  const [year, make, ...modelParts] = item.title.split(" ");
  const listingDetails = String(item.id) === "silverado-lt" ? silveradoListingDetails : null;
  return {
    id: item.id,
    vehicleId: item.id,
    name: item.title,
    vehicleName: item.title,
    year,
    make,
    model: modelParts.join(" "),
    price: item.price,
    mileage: item.mileage,
    drivetrain: item.drivetrain,
    fuel: item.fuel,
    color: item.color,
    status: "READY",
    inventoryStatus: item.status,
    mainPhoto: item.mainPhoto,
    photos: item.photos,
    listingDetails,
    room: "Front line",
    roomNumber: "Front line",
    careLevel: item.drivetrain,
    careLevelLabel: `${item.drivetrain} ${item.fuel}`,
    ncpStatus: "READY",
    ncpStatusLabel: "Ready for sale",
    nextReview: "Today",
    admissionDate: "Front-line ready",
    primaryContactName: "Auto Portal",
    primaryContactRelationship: "Sales desk",
    primaryContactPhone: "",
    primaryContactEmail: "",
    locationInformation: {
      addressLine1: "",
      city: "",
      state: "",
    },
    notes: `${item.title} is listed at ${item.price} with ${item.mileage}.`,
    careNeeded: "Photos, pricing, customer follow-up, and deal paperwork.",
    medicationNotes: `${item.fuel} | ${item.drivetrain}`,
    behaviorSafetyNotes: `${item.color} exterior. ${item.status}.`,
    openAlertCount: 0,
    stateAppraisalImported: true,
    stateAppraisalFileName: `${item.title} appraisal.pdf`,
  };
};
const completedVehicleStatuses = new Set(["ACTIVE", "ONBOARDED", "SUBMITTED", "COMPLETED", "COMPLETE", "CONFIRMED", "CURRENT", "READY", "AVAILABLE"]);
const incompleteVehicleStatuses = new Set(["PROSPECTIVE", "IN_PROGRESS", "PENDING", "DRAFT", "NEW"]);
const vehicleHasCompletedOnboarding = (vehicle = {}, preview = null) => {
  const vehicleStatus = normalizeStatusKey(vehicle.status || vehicle.onboardingStatus);
  const previewStatus = normalizeStatusKey(
    preview?.onboardingStepStatus?.status ||
    preview?.onboardingStatus ||
    preview?.status ||
    preview?.vehicle?.status,
  );
  if (completedVehicleStatuses.has(vehicleStatus) || completedVehicleStatuses.has(previewStatus)) return true;
  if (incompleteVehicleStatuses.has(vehicleStatus) || incompleteVehicleStatuses.has(previewStatus)) return false;
  return false;
};
const pickActiveAppraisalProcess = (processes = []) => {
  const list = Array.isArray(processes) ? processes : [];
  return [...list]
    .filter((process) => process && !isClosedAppraisalProcess(process))
    .sort((a, b) => processDateValue(b) - processDateValue(a))[0] || null;
};
const appraisalRedirectIdFor = ({ processes = [], appraisals = [], preview = null, vehicle = {} }) => {
  const activeProcess = pickActiveAppraisalProcess(processes);
  const linkedAppraisal = (appraisals || []).find((appraisal) =>
    appraisal?.appraisalProcessId || appraisal?.processId
  );
  return (
    activeProcess?.id ||
    activeProcess?.appraisalProcessId ||
    activeProcess?.processId ||
    preview?.appraisalProcessId ||
    preview?.previewPayload?.appraisalProcessId ||
    vehicle?.activeAppraisalProcessId ||
    vehicle?.currentAppraisalProcessId ||
    vehicle?.openAppraisalProcessId ||
    linkedAppraisal?.appraisalProcessId ||
    linkedAppraisal?.processId ||
    ""
  );
};
const stageIndexFor = (stages = [], stageKey = "") => {
  const normalized = normalizeStatusKey(stageKey);
  return stages.findIndex((stage) => (stage.stageKeys || []).map(normalizeStatusKey).includes(normalized));
};
const vehicleAppraisalPatientType = (appraisal = {}) => {
  const raw = normalizeStatusKey(appraisal.patientType || appraisal.vehicleType || appraisal.type);
  if (raw.includes("NEW")) return "NEW";
  if (raw.includes("ANNUAL") || raw.includes("RESIDENT")) return "RESIDENT";
  return raw || "Not set";
};
const vehicleAppraisalTitle = (appraisal = {}) =>
  appraisal.name || appraisal.title || labelize(appraisal.type || appraisal.appraisalType || "Vehicle Appraisal");
const vehicleAppraisalSocialWorker = (appraisal = {}) =>
  appraisal.socialWorkerName ||
  [appraisal.socialWorkerFirstName, appraisal.socialWorkerLastName].filter(Boolean).join(" ") ||
  appraisal.socialWorker?.name ||
  "Not assigned";
const vehicleAppraisalVehicleId = (appraisal = {}) =>
  appraisal.vehicleId ||
  appraisal.clientId ||
  appraisal.vehicle?.id ||
  appraisal.vehicle?.vehicleId ||
  appraisal.vehicleData?.id ||
  appraisal.vehicleData?.vehicleId ||
  appraisal.vehicleDetails?.id ||
  appraisal.vehicleDetails?.vehicleId ||
  appraisal.stageReferences?.vehicleId ||
  appraisal.references?.vehicleId ||
  "";
const vehicleAppraisalVehicleName = (appraisal = {}) =>
  appraisal.vehicleName ||
  appraisal.vehicleFullName ||
  appraisal.clientName ||
  appraisal.vehicle?.name ||
  appraisal.vehicle?.fullName ||
  [appraisal.vehicle?.firstName, appraisal.vehicle?.middleName, appraisal.vehicle?.lastName].filter(Boolean).join(" ") ||
  "";
const vehicleAppraisalRecordItems = (appraisal = {}) => [
  vehicleAppraisalTitle(appraisal),
  appraisal.uploadedDocuments ? "Uploaded documents" : "",
  appraisal.signedDocuments ? "Signed documents" : "",
  appraisal.meetingDate ? "Meeting details" : "",
  appraisal.summary ? "Appraisal summary" : "",
].filter(Boolean);
const linkedAppraisalProcessIdFor = (appraisal = {}) =>
  appraisal.appraisalProcessId || appraisal.processId || "";
const appraisalProcessIdFor = (appraisal = {}) =>
  linkedAppraisalProcessIdFor(appraisal) || appraisal.id || appraisal.appraisalId || "";
const appraisalProcessRouteIdFor = (appraisal = {}) =>
  linkedAppraisalProcessIdFor(appraisal) || (appraisal.__appraisalProcessSource ? appraisal.id : "");
const vehicleAppraisalDedupeKey = (appraisal = {}) => {
  const appraisalProcessId = linkedAppraisalProcessIdFor(appraisal);
  if (appraisalProcessId) return `appraisal-process:${appraisalProcessId}`;
  return [
    "vehicle-appraisal",
    vehicleAppraisalVehicleId(appraisal) || vehicleAppraisalVehicleName(appraisal),
    vehicleAppraisalPatientType(appraisal),
    appraisal.processStartDate || appraisal.appraisalDate || appraisal.startDate || "",
    vehicleAppraisalTitle(appraisal),
  ].map((value) => String(value || "").trim().toLowerCase()).join("|");
};
const dedupeVehicleAppraisals = (items = []) => {
  const byKey = new Map();
  (Array.isArray(items) ? items : []).forEach((appraisal) => {
    const key = vehicleAppraisalDedupeKey(appraisal);
    const existing = byKey.get(key);
    if (!existing || vehicleAppraisalDateValue(appraisal) >= vehicleAppraisalDateValue(existing)) {
      byKey.set(key, appraisal);
    }
  });
  return Array.from(byKey.values());
};
const appraisalProcessTitle = (appraisal = {}) =>
  appraisal.name ||
  appraisal.title ||
  appraisal.processName ||
  appraisal.appraisalName ||
  labelize(appraisal.type || appraisal.appraisalType || "Vehicle Appraisal");
const appraisalProcessRecords = (appraisal = {}) => {
  const records = appraisal.attachedRecords || appraisal.records || appraisal.appraisalRecords || [];
  if (Array.isArray(records)) {
    return records.map((record) => (typeof record === "string" ? record : record?.name || record?.title || record?.recordName || record?.recordType)).filter(Boolean);
  }
  if (typeof records === "string" && records.trim()) {
    return records.split(",").map((record) => record.trim()).filter(Boolean);
  }
  return vehicleAppraisalRecordItems(appraisal);
};
const isClosedVehicleAppraisal = (appraisal = {}) =>
  closedAppraisalStatuses.includes(normalizeStatusKey(appraisalWorkflowStatus(appraisal)));
const isActiveVehicleAppraisal = (appraisal = {}) => {
  const status = normalizeStatusKey(appraisal.rawWorkflowStatus || appraisalWorkflowStatus(appraisal));
  if (status) return !closedAppraisalStatuses.includes(status);
  return Boolean(appraisal.__appraisalProcessSource || appraisalProcessRouteIdFor(appraisal));
};
const vehicleAppraisalDateValue = (appraisal = {}) => {
  const value = appraisal.updatedAt || appraisal.appraisalDate || appraisal.processStartDate || appraisal.startDate || appraisal.createdAt || "";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? 0 : date.getTime();
};
const mapVehicleAppraisalRecord = (appraisal = {}) => ({
  ...appraisal,
  rawWorkflowStatus: appraisalWorkflowStatus(appraisal),
  id: appraisalProcessIdFor(appraisal) || appraisal.id,
  processName: appraisalProcessTitle(appraisal),
  title: appraisalProcessTitle(appraisal),
  summary: appraisal.summary || appraisal.description || appraisal.careNeeded || appraisal.diagnosisHealthProfile || appraisal.meetAndGreetNotes || (appraisal.__appraisalProcessSource ? "Appraisal process from auto service." : "Vehicle appraisal record from auto service."),
  patientType: vehicleAppraisalPatientType(appraisal),
  startDate: appraisal.processStartDate || appraisal.startDate || appraisal.appraisalDate || appraisal.createdAt,
  endDate: appraisal.processEndDate || appraisal.endDate,
  status: labelize(appraisalWorkflowStatus(appraisal) || "Draft"),
  dateAdded: appraisal.appraisalDate || appraisal.processStartDate || appraisal.startDate || appraisal.createdAt || appraisal.updatedAt,
  attachedRecords: appraisalProcessRecords(appraisal),
  socialWorker: vehicleAppraisalSocialWorker(appraisal),
  addedBy: appraisal.assessorName || appraisal.createdByName || "System",
  routePath: appraisalProcessRouteIdFor(appraisal) ? `/appraisals/${appraisalProcessRouteIdFor(appraisal)}/new` : "",
});
const asList = (value) => {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.content)) return value.content;
  if (Array.isArray(value?.data)) return value.data;
  if (Array.isArray(value?.items)) return value.items;
  return [];
};
const stripHtml = (value) => String(value || "").replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
const firstText = (...values) => values.find((value) => value !== null && value !== undefined && value !== "") || "";
const recordDate = (record = {}) =>
  firstText(record.dateAdded, record.happenedAt, record.occurredAt, record.noteDate, record.createdAt, record.updatedAt, record.uploadedAt, record.appraisalDate);
const addedByFor = (record = {}) =>
  firstText(record.addedByName, record.createdByName, record.uploadedByName, record.assessorName, record.caregiverName, record.staffName, record.addedBy, record.createdBy, "System");
const mapDailyNoteRecord = (note = {}) => ({
  ...note,
  title: firstText(note.title, labelize(note.category), "Daily note"),
  summary: stripHtml(firstText(note.summary, note.notes, note.note, note.description, note.evaluationNotes)) || "No note summary added.",
  dateAdded: recordDate(note) || new Date().toISOString(),
  addedBy: addedByFor(note),
  category: labelize(firstText(note.category, note.noteCategory, note.type, "Note")),
});
const mapDocumentRecord = (document = {}) => ({
  ...document,
  title: firstText(document.title, document.name, document.fileName, document.originalFileName, "Document"),
  summary: firstText(document.description, document.summary, document.fileName, document.storageStatus, "Vehicle document"),
  dateAdded: recordDate(document) || new Date().toISOString(),
  addedBy: addedByFor(document),
  status: labelize(firstText(document.status, document.storageStatus, document.documentStatus, "Available")),
});
const mapCareTeamRecord = (member = {}) => ({
  ...member,
  title: firstText(member.name, member.staffName, [member.firstName, member.lastName].filter(Boolean).join(" "), member.organizationName, member.email, "Sales team member"),
  summary: firstText(member.summary, member.assignment, member.description, member.email, phonesSummary(member.phones), member.phone, "Sales team contact"),
  dateAdded: recordDate(member) || new Date().toISOString(),
  addedBy: addedByFor(member),
  role: labelize(firstText(member.role, member.assignmentRole, member.relationship, member.contactType, "Sales Team")),
});
const mapCarePlanRecord = (plan = {}) => ({
  ...plan,
  appraisalId: firstText(plan.appraisalId, plan.appraisalProcessId, plan.processId),
  title: firstText(plan.title, plan.name, plan.planName, "Negotiated campaign"),
  summary: firstText(plan.summary, plan.description, plan.notes, plan.carePlanNotes, "Negotiated campaign record"),
  dateAdded: recordDate(plan) || new Date().toISOString(),
  socialWorker: vehicleAppraisalSocialWorker(plan),
  status: labelize(firstText(plan.status, plan.workflowStatus, plan.planStatus, "Draft")),
});
const mapPreliminaryPlanRecord = (record = {}) => ({
  ...record,
  title: firstText(record.title, record.name, record.recordName, "Preliminary plan"),
  summary: firstText(record.summary, record.description, record.notes, record.planSummary, "Preliminary plan record"),
  dateAdded: recordDate(record) || new Date().toISOString(),
  addedBy: addedByFor(record),
  status: labelize(firstText(record.status, record.workflowStatus, record.recordStatus, "Draft")),
});
const isPreliminaryPlanRecord = (record = {}) => {
  const key = normalizeStatusKey(firstText(record.recordType, record.type, record.stage, record.workflowStage, record.title, record.name));
  return key.includes("PRELIMINARY");
};
const splitCurrentAndPrevious = (records = [], isClosed = null) => {
  const sorted = sortedRecordRows(records);
  const current = sorted.find((record) => !isClosed || !isClosed(record)) || null;
  return {
    current,
    previous: current ? sorted.filter((record) => record !== current) : sorted,
  };
};

const assignedStaffToday = [
  {
    key: "staff-sarah-mitchell",
    name: "Sarah Mitchell",
    role: "Sales lead",
    shift: "7:00 AM - 3:00 PM",
    phone: "(206) 555-0173",
    email: "sarah.mitchell@example.com",
    assignment: "Morning medications, mobility support, daily notes",
  },
  {
    key: "staff-daniel-reed",
    name: "Daniel Reed",
    role: "Sales associate",
    shift: "3:00 PM - 11:00 PM",
    phone: "(206) 555-0197",
    email: "daniel.reed@example.com",
    assignment: "Evening sales coverage and customer follow-up",
  },
];

const dateInputValue = (date) => new Date(date).toISOString().slice(0, 10);

const shiftDate = (date, days) => {
  const nextDate = new Date(date);
  nextDate.setDate(nextDate.getDate() + days);
  return nextDate;
};

const todayDate = dateInputValue(new Date());
const yesterdayDate = dateInputValue(shiftDate(todayDate, -1));
const olderNoteDate = dateInputValue(shiftDate(todayDate, -4));

const negotiatedPlanSources = [
  ["Preliminary Plan", "Admission preliminary plan", "May 7, 2024", "Included", "ti-heart-handshake"],
  ["State Appraisal", "Initial state appraisal packet", "May 6, 2024", "Included", "ti-clipboard-text"],
  ["Daily Notes", "30-day observation notes and discrepancies", "May 20, 2024", "Included", "ti-notes"],
  ["MaG", "Meet-and-Greet hospital evaluation", "May 6, 2024", "Included", "ti-user-check"],
  ["Family Inputs", "PoA and family vehicle preferences", "May 7, 2024", "Included", "ti-users-group"],
  ["Sales Team Updates", "PCP, PT, social worker, and internal staff updates", "May 24, 2024", "Pending review", "ti-stethoscope"],
];

const drawerContent = {
  ...Object.fromEntries(assignedStaffToday.map((staff) => [
    staff.key,
    {
      title: staff.name,
      intro: `${staff.role} assigned to Margaret Johnson today.`,
      sections: [
        {
          title: "Staff Details",
          rows: [
            ["Name", staff.name],
            ["Role", staff.role],
            ["Shift", staff.shift],
            ["Assignment", staff.assignment],
          ],
        },
        {
          title: "Contact Information",
          rows: [
            ["Phone", staff.phone],
            ["Email", staff.email],
          ],
        },
      ],
      contactActions: [
        ["Chat", "ti-message-circle", "#chat"],
        ["Call", "ti-phone", `tel:${staff.phone.replace(/[^+\d]/g, "")}`],
      ],
      action: "View schedule",
    },
  ])),
  persona: {
    title: "Vehicle Persona",
    intro: "Vehicle bio data, contact information, and next of kin details.",
    sections: [
      {
        title: "Bio Data",
        rows: [
          ["Full name", "Margaret Johnson"],
          ["Date of birth", "August 14, 1941"],
          ["Age", "82"],
          ["Gender", "Female"],
          ["Room", "214A"],
          ["Admission date", "May 6, 2024"],
        ],
      },
      {
        title: "Contact Information",
        rows: [
          ["Phone", "(206) 555-0184"],
          ["Email", "margaret.johnson@example.com"],
          ["Address", "4821 Cedar Lane, Seattle, WA 98112"],
          ["Preferred contact", "Phone through PoA"],
        ],
      },
      {
        title: "Next of Kin",
        rows: [
          ["Name", "Elaine Johnson"],
          ["Relationship", "Daughter / PoA"],
          ["Phone", "(206) 555-0128"],
          ["Email", "elaine.johnson@example.com"],
        ],
      },
    ],
    action: "Edit persona",
  },
  notes: {
    title: "Daily Notes",
    intro: "Capture daily behavior, health, welfare, discrepancies, and changes for Margaret Johnson.",
    rows: [
      ["Today at 9:15 AM", "Mood stable after breakfast. Mild shortness of breath noted during hallway walk."],
      ["Yesterday at 4:30 PM", "Family shared that door should remain slightly open during sleep."],
      ["May 12 at 11:20 AM", "Transfer assistance updated from independent to one-person assist."],
    ],
    action: "Add daily note",
  },
  documents: {
    title: "Files and Documents",
    intro: "Review signed paperwork, state appraisal files, PoA documents, and confirmed campaign files.",
    rows: [
      ["State Initial Appraisal.pdf", "Uploaded May 6, 2024"],
      ["Admission Agreements.pdf", "Signed May 7, 2024"],
      ["PoA Documentation.pdf", "Verified May 7, 2024"],
      ["Negotiated Campaign Draft.pdf", "Updated May 24, 2024"],
    ],
    action: "Upload file",
  },
  appraisals: {
    title: "Appraisals",
    intro: "Review state appraisals, annual appraisal changes, and appraisal items that affect campaignning.",
    rows: [
      ["Initial appraisal", "Vehicle history, condition, and sales needs imported."],
      ["MaG evaluation", "Provider review notes show transfer discrepancy."],
      ["Final appraisal update", "Pending final provider input before state confirmation."],
    ],
    action: "Add new appraisal",
  },
  appraisalCreate: {
    title: "New Appraisal",
    intro: "Add a state appraisal record, attach the source files, and capture the campaignning details that should feed the vehicle record.",
    form: "appraisal",
  },
  carePlan: {
    title: "Negotiated Campaigns",
    intro: "Manage 30-day observations, preliminary plans, provider inputs, pay negotiation, and state confirmation.",
    rows: [
      ["30-day probation", "18 notes captured; discrepancies documented."],
      ["Preliminary campaign", "Created after the 30-day notes are reviewed."],
      ["Final NCP", "Ready for provider review and social worker negotiation."],
    ],
    action: "Open NCP workspace",
  },
  team: {
    title: "Sales Team",
    intro: "Coordinate updates with PCP, therapy, social worker, PoA/family, and internal dealer team.",
    rows: [
      ["PCP", "Dr. Helen Morris"],
      ["Physical Therapist", "Jason Reed"],
      ["PoA / Family", "Elaine Johnson"],
      ["Social Worker", "Amanda Parker"],
    ],
    action: "Message sales consultant",
  },
  alerts: {
    title: "Incidents and Alerts",
    intro: "Track incidents, health or welfare risks, and follow-up items.",
    rows: [
      ["Fall risk", "High priority; hallway walk supervision required."],
      ["Transfer discrepancy", "State appraisal conflicts with MaG/home observation."],
    ],
    action: "Add alert",
  },
};

const tabItems = {
  appraisal: {
    createLabel: "Start New Appraisal",
    drawerKey: "appraisals",
    createDrawerKey: "appraisalCreate",
    currentLabel: "Current appraisal process",
    gridTemplate: "minmax(180px, 1fr) 105px 120px 120px 115px minmax(190px, 1.1fr) 135px 28px",
    columns: [
      ["Appraisal Process", "processName"],
      ["Patient Type", "patientType", "status"],
      ["Start Date", "startDate", "date"],
      ["End Date", "endDate", "date"],
      ["Status", "status", "status"],
      ["Attached Records", "attachedRecords", "attachedRecords"],
      ["Social Worker", "socialWorker"],
    ],
    current: {
      processName: "New patient appraisal process",
      summary: "New vehicle onboarding from state initial appraisal through residency confirmation.",
      patientType: "NEW",
      startDate: "2024-05-06T10:30:00",
      endDate: "2024-06-05T17:00:00",
      status: "In progress",
      dateAdded: "2024-05-06T10:30:00",
      attachedRecords: ["Initial State Appraisal", "Meet and Greet", "Provider Review", "Client Onboarding", "Vehicle 30-Day Probation", "Daily Notes", "Preliminary Plan", "Negotiated Campaign", "Payment Negotiation", "Final State Appraisal", "State Confirmation", "Ongoing Sales"],
      socialWorker: "Amanda Parker",
      addedBy: "Sarah Mitchell",
    },
    rows: [
      {
        processName: "Vehicle annual appraisal process 2023",
        summary: "Existing vehicle yearly review and updated negotiated plan.",
        patientType: "RESIDENT",
        startDate: "2023-06-12T14:10:00",
        endDate: "2023-07-10T15:00:00",
        status: "Confirmed",
        dateAdded: "2023-06-12T14:10:00",
        attachedRecords: ["Initial Appraisal Review", "Notes", "Negotiated Plan", "Final Appraisal", "Residency Confirmation"],
        socialWorker: "Amanda Parker",
        addedBy: "Amanda Parker",
      },
      {
        processName: "Vehicle change review",
        summary: "Change-of-condition appraisal process for updated support needs.",
        patientType: "RESIDENT",
        startDate: "2023-02-03T09:45:00",
        endDate: "2023-02-21T13:30:00",
        status: "Closed",
        dateAdded: "2023-02-03T09:45:00",
        attachedRecords: ["Initial Appraisal Review", "Notes", "Negotiated Plan", "Final Appraisal"],
        socialWorker: "Martin Lee",
        addedBy: "Sarah Mitchell",
      },
    ],
  },
  "preliminary-plan": {
    createLabel: "Create preliminary plan",
    drawerKey: "carePlan",
    currentLabel: "Current preliminary plan",
    gridTemplate: "minmax(190px, 1fr) 130px 150px 120px 28px",
    columns: [
      ["Plan", "title"],
      ["Created", "dateAdded", "date"],
      ["Prepared By", "addedBy"],
      ["Status", "status", "status"],
    ],
    current: {
      title: "Admission preliminary plan",
      summary: "Provider-created plan after vehicle arrived home.",
      dateAdded: "2024-05-07T09:00:00",
      addedBy: "Sarah Mitchell",
      status: "Active",
    },
    rows: [
      {
        title: "MaG sales assumptions",
        summary: "Initial support assumptions from hospital visit.",
        dateAdded: "2024-05-06T16:45:00",
        addedBy: "Sarah Mitchell",
        status: "Superseded",
      },
    ],
  },
  "negotiated-care-plan": {
    createLabel: "Create negotiated plan",
    drawerKey: "carePlan",
    currentLabel: "Current negotiated campaign",
    gridTemplate: "minmax(200px, 1fr) 130px 145px 120px 28px",
    columns: [
      ["Plan", "title"],
      ["Created", "dateAdded", "date"],
      ["Social Worker", "socialWorker"],
      ["Status", "status", "status"],
    ],
    current: {
      appraisalId: "ap-new-mj-2024",
      title: "Final NCP draft",
      summary: "Generated from 30-day notes and provider inputs.",
      dateAdded: "2024-05-24T08:30:00",
      socialWorker: "Amanda Parker",
      status: "In review",
    },
    rows: [
      {
        title: "30-day observation plan",
        summary: "Discrepancies and deal changes collected.",
        dateAdded: "2024-05-20T11:00:00",
        socialWorker: "Amanda Parker",
        status: "Working",
      },
      {
        title: "Preliminary campaign",
        summary: "Created from appraisal, MaG, paperwork, and family discovery.",
        dateAdded: "2024-05-07T09:00:00",
        socialWorker: "Amanda Parker",
        status: "Complete",
      },
    ],
  },
  "daily-notes": {
    createLabel: "Add daily note",
    drawerKey: "notes",
    currentLabel: "Latest daily note",
    gridTemplate: "minmax(220px, 1fr) 130px 145px 120px 28px",
    columns: [
      ["Note", "title"],
      ["Date Added", "dateAdded", "date"],
      ["Added By", "addedBy"],
      ["Category", "category", "status"],
    ],
    current: {
      title: "Mood and walk observation",
      summary: "Mood stable after breakfast; mild shortness of breath during hallway walk.",
      dateAdded: `${todayDate}T09:15:00`,
      addedBy: "Sarah Mitchell",
      category: "Health",
    },
    rows: [
      {
        title: "Family sleep preference",
        summary: "Door slightly open and hallway light visible improves sleep.",
        dateAdded: `${yesterdayDate}T16:30:00`,
        addedBy: "Sarah Mitchell",
        category: "Welfare",
      },
      {
        title: "Transfer support update",
        summary: "One-person assist needed despite independent transfer appraisal.",
        dateAdded: `${olderNoteDate}T11:20:00`,
        addedBy: "Sarah Mitchell",
        category: "Discrepancy",
      },
    ],
  },
  documents: {
    createLabel: "Upload document",
    drawerKey: "documents",
    currentLabel: "Current document",
    gridTemplate: "minmax(220px, 1fr) 130px 145px 120px 28px",
    columns: [
      ["File", "title"],
      ["Date Added", "dateAdded", "date"],
      ["Uploaded By", "addedBy"],
      ["Status", "status", "status"],
    ],
    current: {
      title: "Negotiated Campaign Draft.pdf",
      summary: "Updated campaign draft.",
      dateAdded: "2024-05-24T08:30:00",
      addedBy: "Sarah Mitchell",
      status: "Draft",
    },
    rows: [
      {
        title: "PoA Documentation.pdf",
        summary: "Verified decision-maker file.",
        dateAdded: "2024-05-07T14:00:00",
        addedBy: "Amanda Parker",
        status: "Verified",
      },
      {
        title: "Admission Agreements.pdf",
        summary: "Signed policies and agreements.",
        dateAdded: "2024-05-07T11:30:00",
        addedBy: "Sarah Mitchell",
        status: "Signed",
      },
      {
        title: "State Initial Appraisal.pdf",
        summary: "State appraisal packet.",
        dateAdded: "2024-05-06T10:30:00",
        addedBy: "Sarah Mitchell",
        status: "Imported",
      },
    ],
  },
  "sales-team": {
    createLabel: "Add sales consultant",
    drawerKey: "team",
    currentLabel: "Primary sales consultant",
    gridTemplate: "minmax(190px, 1fr) 130px 145px 140px 28px",
    columns: [
      ["Team Member", "title"],
      ["Date Added", "dateAdded", "date"],
      ["Added By", "addedBy"],
      ["Role", "role", "status"],
    ],
    current: {
      title: "Amanda Parker",
      summary: "Social worker assigned for NCP negotiation.",
      dateAdded: "2024-05-24T10:00:00",
      addedBy: "Sarah Mitchell",
      role: "Social Worker",
    },
    rows: [
      {
        title: "Jason Reed",
        summary: "Physical therapy added for mobility support.",
        dateAdded: "2024-05-14T09:30:00",
        addedBy: "Sarah Mitchell",
        role: "PT",
      },
      {
        title: "Elaine Johnson",
        summary: "PoA / family contact confirmed.",
        dateAdded: "2024-05-07T13:00:00",
        addedBy: "Sarah Mitchell",
        role: "PoA",
      },
      {
        title: "Dr. Helen Morris",
        summary: "Primary service provider.",
        dateAdded: "2024-05-06T12:00:00",
        addedBy: "Amanda Parker",
        role: "PCP",
      },
    ],
  },
};

const sortedRecordRows = (rows) =>
  [...rows].sort((a, b) => new Date(b.dateAdded).getTime() - new Date(a.dateAdded).getTime());

const recordTitle = (record) => record.period || record.title || record.name || record.file;

const recordSummary = (record) => record.summary || record.socialWorker || record.addedBy || record.status || record.role || record.category;

const recordBadge = (record) => record.status || record.role || record.category || "Current";

const formatDate = (date) =>
  new Date(date).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });

const ncpFormSections = [
  {
    title: "Plan Header",
    fields: [
      ["Provider's Name", "providerName", "text"],
      ["Date NCP Started", "ncpStarted", "date"],
      ["Moved In Date", "movedInDate", "date"],
      ["Date Completed", "dateCompleted", "date"],
      ["Date Discharged", "dateDischarged", "date"],
    ],
  },
  {
    title: "Vehicle Information",
    fields: [
      ["Vehicle's Name", "vehicleName", "text"],
      ["Pronouns", "pronouns", "text"],
      ["Date of Birth / Age", "dobAge", "text"],
      ["Primary Language", "primaryLanguage", "text"],
      ["Speaks English?", "speaksEnglish", "select", ["Yes", "No"]],
      ["Interpreter Needed?", "interpreterNeeded", "select", ["No", "Yes"]],
      ["Allergies", "allergies", "textarea"],
    ],
  },
  {
    title: "Legal, Specialty, and Evacuation",
    fields: [
      ["Legal Documents", "legalDocuments", "checkboxes", ["None", "Advanced Directives", "POLST Form", "Other"]],
      ["Specialty Needs", "specialtyNeeds", "checkboxes", ["No", "Dementia", "Mental Health", "Developmental Disability", "Other"]],
      ["Evacuation Assistance Required", "evacuationAssistance", "select", ["None - vehicle is independent", "Assistance required"]],
      ["Vehicle Evacuation and Safety Instructions", "evacuationInstructions", "textarea"],
      ["Mental/Physical Health - Current Medical Status/Diagnosis", "medicalStatus", "textarea"],
    ],
  },
  {
    title: "Responsible Parties - Contacts",
    fields: [
      ["Case Manager", "caseManager", "text"],
      ["DPOA / Guardian", "guardian", "text"],
      ["Medical or Mental Health Provider", "healthProvider", "text"],
      ["Pharmacy", "pharmacy", "text"],
      ["Preferred Contact Method", "preferredContact", "select", ["Phone", "Cell", "Email", "Fax"]],
      ["Contact Notes", "contactNotes", "textarea"],
    ],
  },
  {
    title: "Communication",
    fields: [
      ["Modes of Expression", "modesOfExpression", "textarea"],
      ["Problems with Mode of Expression?", "expressionProblems", "select", ["No", "Yes"]],
      ["Speech/Hearing/Vision Equipment", "communicationEquipment", "textarea"],
      ["How vehicle makes self-understood", "selfUnderstood", "textarea"],
      ["How vehicle understands others", "understandsOthers", "textarea"],
      ["Ability to Use Phone", "phoneAbility", "select", ["Independent", "Assistance Needed", "Dependent"]],
      ["Communication Sales Consultant Instructions", "communicationInstructions", "textarea"],
    ],
  },
  {
    title: "Medication Management",
    fields: [
      ["Medication Overview", "medicationOverview", "checkboxes", ["Medication allergies", "More than one medication assistance need", "Psychopharmacologic medications", "See MAR"]],
      ["Meds Ordered By", "medsOrderedBy", "text"],
      ["Meds Delivered By", "medsDeliveredBy", "text"],
      ["Medication Assistance Type", "medicationAssistanceType", "select", ["Self-administration", "Self-administration with assistance", "Medication administration"]],
      ["Medication Types", "medicationTypes", "checkboxes", ["Oral", "Topical", "Eye drops/ointment", "Inhalers", "Sprays", "Injections", "Allergy Kits", "Suppositories", "Other"]],
      ["Nurse Delegation / RN Delegator", "nurseDelegation", "textarea"],
      ["Medication Strengths and Abilities", "medicationStrengths", "textarea"],
      ["Medication Assistance Required / Sales Consultant Will", "medicationCaregiverInstructions", "textarea"],
      ["Medication Refusal Plan", "medicationRefusalPlan", "textarea"],
    ],
  },
  {
    title: "Health, Treatments, and Therapies",
    fields: [
      ["Health Indicators", "healthIndicators", "checkboxes", ["Pain", "Weight loss/gain", "Vital signs", "Hospitalization or ER visits", "Other"]],
      ["Pain Impact / Current Weight / Height / Notes", "healthIndicatorNotes", "textarea"],
      ["Treatments / Programs / Therapies", "treatments", "checkboxes", ["Oxygen Use", "Dialysis", "Blood Thinners", "INR/LAB", "Blood Glucose Monitoring", "Injection", "CPAP/BIPAP", "Nebulizer", "PT/OT/ST", "Home Health", "Adult Day Health", "Hospice", "Meaningful Day"]],
      ["Treatment Provider / Vendor / Agency Details", "treatmentDetails", "textarea"],
      ["Treatment Assistance Required / Sales Consultant Will", "treatmentAssistance", "textarea"],
      ["Treatment Refusal Plan", "treatmentRefusalPlan", "textarea"],
    ],
  },
  {
    title: "Psych/Social/Cognitive Status",
    fields: [
      ["Sleep and Nighttime Support", "sleepSupport", "textarea"],
      ["Memory Impairment", "memoryImpairment", "checkboxes", ["Short-term", "Long-term", "Oriented to person"]],
      ["Behaviors", "behaviors", "checkboxes", ["Impaired decision making", "Disruptive behavior", "Assaultive", "Resistive to service", "Depression", "Anxiety", "Irritability", "Disorientation", "Wandering/Pacing", "Exit seeking", "Hallucinations", "Delusions", "Unsafe behavior", "Other"]],
      ["Behavior Support / Crisis Plan", "behaviorSupport", "textarea"],
      ["Mental Health Provider / Program", "mentalHealthProvider", "textarea"],
      ["Typical Day Narrative", "typicalDay", "textarea"],
      ["Left Alone Plan", "leftAlonePlan", "textarea"],
      ["Universal Precautions", "universalPrecautions", "textarea"],
    ],
  },
  {
    title: "Activities of Daily Living",
    fields: [
      ["Functional Limitations Impacting ADLs", "adlLimitations", "textarea"],
      ["Ambulation / Mobility", "ambulation", "select", ["Independent", "Supervision/Cueing", "Assistance Needed", "Totally Dependent"]],
      ["Bed Mobility / Transfer", "transfer", "select", ["Independent", "Supervision/Cueing", "Assistance Needed", "Totally Dependent"]],
      ["Eating", "eating", "select", ["Independent", "Supervision/Cueing", "Assistance Needed", "Totally Dependent"]],
      ["Toileting / Continence", "toileting", "select", ["Independent", "Supervision/Cueing", "Assistance Needed", "Totally Dependent"]],
      ["Dressing", "dressing", "select", ["Independent", "Supervision/Cueing", "Assistance Needed", "Totally Dependent"]],
      ["Personal Hygiene", "personalHygiene", "select", ["Independent", "Supervision/Cueing", "Assistance Needed", "Totally Dependent"]],
      ["Bathing", "bathing", "select", ["Independent", "Supervision/Cueing", "Assistance Needed", "Totally Dependent"]],
      ["Tire condition", "footCare", "select", ["Not listed", "Needs review", "Good", "Excellent"]],
      ["Paint condition", "skinCare", "select", ["Not listed", "Needs review", "Good", "Excellent"]],
      ["ADL Sales Consultant Instructions", "adlInstructions", "textarea"],
    ],
  },
  {
    title: "Instrumental ADLs and Activities",
    fields: [
      ["Managing Finances", "finances", "select", ["Independent", "Assistance Needed", "Dependent"]],
      ["Shopping", "shopping", "select", ["Independent", "Assistance Needed", "Dependent"]],
      ["Transportation", "transportation", "select", ["Independent", "Assistance Needed", "Dependent"]],
      ["Activities / Social", "activitiesSocial", "select", ["Independent", "Assistance Needed", "Dependent"]],
      ["Activity Preferences", "activityPreferences", "checkboxes", ["Reading", "Phone conversation/visiting", "Bible study or church", "Visitors", "Gardening", "Outings", "Exercises/range of motion", "Cooking or baking", "Watching TV/movies", "Arts and crafts", "Games/cards/puzzles", "Music", "Community integration", "Other"]],
      ["Activity Narrative", "activityNarrative", "textarea"],
      ["Smoking Plan", "smokingPlan", "textarea"],
      ["Case Management Contact / When CM Will Be Contacted", "caseManagement", "textarea"],
      ["Other Issues / Concerns / Problems", "otherConcerns", "textarea"],
    ],
  },
  {
    title: "Review and Approval",
    fields: [
      ["Date of Original Plan", "originalPlanDate", "date"],
      ["Review / Revision Dates", "revisionDates", "textarea"],
      ["Involved in NCP Development", "involved", "checkboxes", ["Vehicle", "Vehicle Representative", "Parent", "Health Professional", "Other"]],
      ["Person Approving Plan", "approvingPerson", "text"],
      ["Vehicle Verbally Agreed to NCP", "verbalAgreement", "select", ["No", "Yes"]],
      ["NCP Sent to DSHS CM On", "sentToDshs", "date"],
      ["Vehicle Recommendations", "vehicleRecommendations", "textarea"],
    ],
  },
];

const isWithinDateRange = (date, startDate, endDate) => {
  const target = dateInputValue(date);
  return target >= startDate && target <= endDate;
};

function RecordValue({ record, column }) {
  const [, key, type] = column;
  const value = record[key];

  if (type === "date") {
    return <span>{formatDate(value)}</span>;
  }

  if (type === "files") {
    return (
      <span className="appraisal-file-stack">
        {value.map((file) => (
          <small key={file}><i className="ti ti-paperclip" /> {file}</small>
        ))}
      </span>
    );
  }

  if (type === "attachedRecords") {
    return (
      <span className="appraisal-record-stack">
        {value.map((record) => (
          <small key={record}>{record}</small>
        ))}
      </span>
    );
  }

  if (type === "status") {
    return <em>{value}</em>;
  }

  if (key === "period" || key === "processName" || key === "title") {
    return <span><strong>{value}</strong>{record.summary ? <small>{record.summary}</small> : null}</span>;
  }

  return <span>{value}</span>;
}

function VehicleStarRating({ rating = 0 }) {
  return (
    <span className="vehicle-star-rating" aria-label={`${rating} out of 5 stars`}>
      {[1, 2, 3, 4, 5].map((star) => (
        <span className={star <= rating ? "vehicle-star-filled" : "vehicle-star-empty"} key={star}>★</span>
      ))}
    </span>
  );
}

const unavailableDetailValues = new Set([
  "any price",
  "color pending",
  "details pending",
  "drivetrain pending",
  "fuel pending",
  "mileage pending",
  "not assigned",
  "not listed",
  "not set",
  "price pending",
]);

const isListedDetailValue = (value) => {
  if (Array.isArray(value)) return value.some(isListedDetailValue);
  if (value === null || value === undefined) return false;
  const text = String(value).trim();
  return Boolean(text) && !unavailableDetailValues.has(text.toLowerCase());
};

function VehicleSpecGrid({ items = [] }) {
  const listedItems = items.filter(([, value]) => isListedDetailValue(value));
  if (!listedItems.length) return null;

  return (
    <div className="vehicle-spec-grid">
      {listedItems.map(([label, value]) => (
        <div className="vehicle-spec-row" key={label}>
          <strong>{label}:</strong>
          <span>{value}</span>
        </div>
      ))}
    </div>
  );
}

const vehiclePhotoUrl = (value) => {
  if (typeof value === "string") {
    const trimmed = value.trim();
    return /^(https?:|\/)/i.test(trimmed) ? trimmed : "";
  }
  if (value && typeof value === "object") {
    return vehiclePhotoUrl(
      value.signedUrl ||
      value.url ||
      value.photoUrl ||
      value.imageUrl ||
      value.downloadUrl ||
      value.fileUrl ||
      value.publicUrl ||
      "",
    );
  }
  return "";
};

const vehiclePhotoUrls = (vehicle = {}) => [
  vehiclePhotoUrl(vehicle?.mainPhoto),
  ...(Array.isArray(vehicle?.photos) ? vehicle.photos.map(vehiclePhotoUrl) : []),
].filter(Boolean);

export function VehicleListingDetails({ details, vehicle, onAction, onInquiry, onManagementAction, managementActionLoading, showMarkActive, publicMode = false, hideFeaturesSection = false }) {
  const [selectedPhoto, setSelectedPhoto] = useState("");
  const activeThumbRef = useRef(null);
  const thumbsRef = useRef(null);
  const photos = vehiclePhotoUrls(vehicle);
  const galleryPhotos = [...new Set(photos)];
  const activePhoto = selectedPhoto && galleryPhotos.includes(selectedPhoto) ? selectedPhoto : galleryPhotos[0];
  const activePhotoIndex = Math.max(0, galleryPhotos.indexOf(activePhoto));
  const showGalleryNav = galleryPhotos.length > 1;
  const shiftGalleryPhoto = (direction) => {
    const nextIndex = (activePhotoIndex + direction + galleryPhotos.length) % galleryPhotos.length;
    setSelectedPhoto(galleryPhotos[nextIndex]);
  };

  useEffect(() => {
    const container = thumbsRef.current;
    const thumb = activeThumbRef.current;
    if (!container || !thumb) return;
    container.scrollTo({
      behavior: "smooth",
      left: thumb.offsetLeft - (container.clientWidth - thumb.clientWidth) / 2,
      top: thumb.offsetTop - (container.clientHeight - thumb.clientHeight) / 2,
    });
  }, [activePhoto]);

  if (!details) return null;

  const featureItems = (details.features || []).filter(([, value]) => isListedDetailValue(value));
  const overviewItems = (details.overview || []).filter(([, value]) => isListedDetailValue(value));
  const fuelEconomyItems = (details.fuelEconomy || []).filter(([, value]) => isListedDetailValue(value));
  const performanceItems = (details.performance || []).filter(([, value]) => isListedDetailValue(value));
  const measurementItems = (details.measurements || []).filter(([, value]) => isListedDetailValue(value));
  const safetyRatings = (details.safetyRatings || []).filter(([, rating]) => isListedDetailValue(rating));
  const safetyFeatures = (details.safetyFeatures || []).filter(isListedDetailValue);
  const optionItems = (details.options || []).filter(isListedDetailValue);

  return (
    <section className="vehicle-listing-details" id="overview">
      <div className="vehicle-listing-top">
        <div className="vehicle-listing-actions" aria-label="Vehicle actions">
          {!publicMode ? (
            <>
              <button className="mini-btn action-note" type="button" onClick={() => onAction("notes")}><i className="ti ti-note" /> Sales Notes</button>
              <button className="mini-btn action-print" type="button" onClick={() => window.print()}><i className="ti ti-printer" /> Print Summary</button>
            </>
          ) : null}
          {publicMode ? (
            <button className="mini-btn action-availability" type="button" onClick={onInquiry}>
              <i className="ti ti-circle-check" /> Check Availability
            </button>
          ) : null}
          {publicMode ? (
            <>
              <a className="mini-btn action-call" href="tel:2533254800"><i className="ti ti-phone" /> Call Now</a>
              <a className="mini-btn action-whatsapp" href="https://wa.me/12533254800"><i className="ti ti-brand-whatsapp" /> WhatsApp</a>
            </>
          ) : null}
          {!publicMode ? <div className="vehicle-management-actions" aria-label="Vehicle management actions">
            <button className="mini-btn action-update" disabled={Boolean(managementActionLoading)} type="button" onClick={() => onManagementAction("update")}>
              <i className="ti ti-edit" /> Update
            </button>
            {showMarkActive ? (
              <button className="mini-btn action-active" disabled={Boolean(managementActionLoading)} type="button" onClick={() => onManagementAction("active")}>
                <i className="ti ti-circle-check" /> Mark as Active
              </button>
            ) : (
              <button className="mini-btn action-pending" disabled={Boolean(managementActionLoading)} type="button" onClick={() => onManagementAction("pending")}>
                <i className="ti ti-clock-hour-4" /> Mark as Pending
              </button>
            )}
            <button className="mini-btn action-sold" disabled={Boolean(managementActionLoading)} type="button" onClick={() => onManagementAction("sold")}>
              <i className="ti ti-rosette-discount-check" /> Mark as Sold
            </button>
            <button className="mini-btn action-remove" disabled={Boolean(managementActionLoading)} type="button" onClick={() => onManagementAction("remove")}>
              <i className="ti ti-archive" /> Remove Vehicle
            </button>
            <button className="mini-btn action-delete" disabled={Boolean(managementActionLoading)} type="button" onClick={() => onManagementAction("delete")}>
              <i className="ti ti-trash" /> Delete
            </button>
          </div> : null}
        </div>

        <div className="vehicle-detail-gallery" aria-label={`${vehicle?.name || vehicle?.vehicleName || "Vehicle"} photo gallery`}>
          <div className="vehicle-detail-gallery-main">
            {showGalleryNav ? (
              <button
                className="vehicle-detail-gallery-nav is-prev"
                type="button"
                onClick={() => shiftGalleryPhoto(-1)}
                aria-label="Previous vehicle photo"
              >
                <i className="ti ti-chevron-left" />
              </button>
            ) : null}
            <VehicleImage src={activePhoto} alt={vehicle?.name || vehicle?.vehicleName || "Vehicle"} />
            {showGalleryNav ? (
              <button
                className="vehicle-detail-gallery-nav is-next"
                type="button"
                onClick={() => shiftGalleryPhoto(1)}
                aria-label="Next vehicle photo"
              >
                <i className="ti ti-chevron-right" />
              </button>
            ) : null}
          </div>
          <div className="vehicle-detail-gallery-thumbs" ref={thumbsRef}>
            {galleryPhotos.map((photo, index) => (
              <button
                aria-label={`Show ${vehicle?.name || vehicle?.vehicleName || "Vehicle"} photo ${index + 1}`}
                className={photo === activePhoto ? "is-active" : ""}
                key={photo}
                onClick={() => setSelectedPhoto(photo)}
                ref={photo === activePhoto ? activeThumbRef : null}
                type="button"
              >
                <VehicleImage src={photo} alt={`${vehicle?.name || vehicle?.vehicleName || "Vehicle"} view ${index + 1}`} />
              </button>
            ))}
          </div>
        </div>
      </div>

      {!hideFeaturesSection && featureItems.length ? (
        <div className="vehicle-listing-section">
          <h2 className="vehicle-listing-title">Features</h2>
          <div className="vehicle-feature-grid">
            {featureItems.map(([label, value, icon]) => (
              <article className="vehicle-feature-item" key={label}>
                <span className="vehicle-feature-icon"><i className={`ti ${icon}`} /></span>
                <div>
                  <strong>{label}</strong>
                  <span>{value}</span>
                </div>
              </article>
            ))}
          </div>
        </div>
      ) : null}

      {overviewItems.length ? (
      <div className="vehicle-listing-section">
        <h2 className="vehicle-listing-title">Overview</h2>
        <VehicleSpecGrid items={overviewItems} />
      </div>
      ) : null}

      {fuelEconomyItems.length ? (
      <div className="vehicle-listing-section vehicle-listing-section-tight">
        <h2 className="vehicle-listing-title">Fuel economy</h2>
        <VehicleSpecGrid items={fuelEconomyItems} />
      </div>
      ) : null}

      {performanceItems.length ? (
      <div className="vehicle-listing-section vehicle-listing-section-tight">
        <h2 className="vehicle-listing-title">Performance</h2>
        <VehicleSpecGrid items={performanceItems} />
      </div>
      ) : null}

      {safetyRatings.length || safetyFeatures.length ? (
      <div className="vehicle-listing-section vehicle-listing-section-compact vehicle-listing-section-tight">
        <h2 className="vehicle-listing-title">Safety</h2>
        <div className="vehicle-safety-grid">
          {safetyRatings.map(([label, rating]) => (
            <div className="vehicle-safety-rating" key={label}>
              <strong>{label}:</strong>
              <VehicleStarRating rating={rating} />
            </div>
          ))}
          {safetyFeatures.map((feature) => (
            <span className="vehicle-safety-feature" key={feature}>{feature}</span>
          ))}
        </div>
      </div>
      ) : null}

      {measurementItems.length ? (
      <div className="vehicle-listing-section vehicle-listing-section-compact">
        <h2 className="vehicle-listing-title">Measurements</h2>
        <VehicleSpecGrid items={measurementItems} />
      </div>
      ) : null}

      {optionItems.length ? (
      <div className="vehicle-listing-section vehicle-listing-section-compact">
        <h2 className="vehicle-listing-title">Options</h2>
        <div className="vehicle-options-grid">
          {optionItems.map((option) => (
            <span key={option}>{option}</span>
          ))}
        </div>
      </div>
      ) : null}
    </section>
  );
}

function NcpField({ field }) {
  const [label, name, type, options = []] = field;

  if (type === "textarea") {
    return (
      <label className="ncp-form-field ncp-form-field-wide">
        <span>{label}</span>
        <textarea name={name} rows={3} />
      </label>
    );
  }

  if (type === "select") {
    return (
      <label className="ncp-form-field">
        <span>{label}</span>
        <select name={name}>
          <option value="">Select</option>
          {options.map((option) => (
            <option value={option} key={option}>{option}</option>
          ))}
        </select>
      </label>
    );
  }

  if (type === "checkboxes") {
    return (
      <fieldset className="ncp-form-field ncp-form-field-wide">
        <legend>{label}</legend>
        <div className="ncp-checkbox-grid">
          {options.map((option) => (
            <label key={option}>
              <input type="checkbox" name={`${name}-${option}`} />
              <span>{option}</span>
            </label>
          ))}
        </div>
      </fieldset>
    );
  }

  return (
    <label className="ncp-form-field">
      <span>{label}</span>
      <input name={name} type={type} />
    </label>
  );
}

const appraisalFormSections = [
  {
    title: "Appraisal Process",
    fields: [
      ["Process name", "processName", "text"],
      ["Patient type", "patientType", "select", ["NEW", "RESIDENT"]],
      ["Start date", "processStartDate", "date"],
      ["End date", "processEndDate", "date"],
      ["Process status", "processStatus", "select", ["Draft", "Active", "In review", "Confirmed", "Closed"]],
      ["Appraisal process owner", "processOwner", "text"],
      ["Attached process records", "attachedProcessRecords", "checkboxes", ["Initial Appraisal", "Meet and Greet", "Initial Appraisal Review", "Client Onboarding", "Vehicle 30-Day Probation", "Notes", "Preliminary Plan", "Negotiated Plan", "Final Appraisal", "Residency Confirmation"]],
    ],
  },
  {
    title: "Appraisal Source",
    fields: [
      ["Appraisal type", "appraisalType", "select", ["Initial state appraisal", "Annual appraisal", "Change-of-condition review", "Final appraisal update", "Other"]],
      ["Appraisal version", "appraisalVersion", "text"],
      ["Appraisal date", "appraisalDate", "date"],
      ["Date received", "dateReceived", "date"],
      ["Social worker", "socialWorker", "text"],
      ["State case ID", "stateCaseId", "text"],
    ],
  },
  {
    title: "Vehicle and Vehicle Snapshot",
    fields: [
      ["Vehicle name", "vehicleName", "text"],
      ["Medication behaviors", "medicationBehaviors", "textarea"],
      ["Diagnosis and history", "diagnosisHistory", "textarea"],
      ["Vehicle needs", "careNeeded", "textarea"],
      ["Medication information", "medicationInformation", "textarea"],
      ["Client information notes", "clientInformation", "textarea"],
    ],
  },
  {
    title: "Attachments and Review",
    fields: [
      ["Appraisal file", "appraisalFile", "file"],
      ["Medication profile file", "medicationProfileFile", "file"],
      ["Supporting files", "supportingFiles", "file"],
      ["Imported from state appraisal", "importedFromState", "select", ["Yes", "No"]],
      ["Reviewed by", "reviewedBy", "text"],
      ["Review status", "reviewStatus", "select", ["Draft", "Ready for provider review", "Reviewed", "Needs follow-up"]],
      ["Provider review notes", "providerReviewNotes", "textarea"],
    ],
  },
];

function AppraisalDrawerForm() {
  return (
    <form className="appraisal-drawer-form">
      {appraisalFormSections.map((section) => (
        <section className="appraisal-drawer-section" key={section.title}>
          <h3>{section.title}</h3>
          <div className="appraisal-drawer-grid">
            {section.fields.map((field) => (
              <NcpField field={field} key={field[1]} />
            ))}
          </div>
        </section>
      ))}
      <div className="appraisal-drawer-actions">
        <button className="btn secondary" type="button">Save draft</button>
        <button className="btn primary" type="button">Add appraisal</button>
      </div>
    </form>
  );
}

function VehicleTabList({ title, intro, tab, onCreate, rowsOverride, currentOverride, loading = false, error = "", showCreate = true }) {
  const config = tabItems[tab];
  const navigate = useNavigate();
  const [noteDateRange, setNoteDateRange] = useState({ start: yesterdayDate, end: todayDate });
  const [carePlanMode, setCarePlanMode] = useState(null);
  const isDailyNotesTab = tab === "daily-notes";
  const isNegotiatedCarePlanTab = tab === "negotiated-care-plan";
  const sourceRows = rowsOverride || config.rows;
  const sourceCurrent = currentOverride === undefined ? config.current : currentOverride;
  const rows = sortedRecordRows(sourceRows).filter((record) =>
    !isDailyNotesTab || isWithinDateRange(record.dateAdded, noteDateRange.start, noteDateRange.end)
  );
  const currentRecord = sourceCurrent && (!isDailyNotesTab || isWithinDateRange(sourceCurrent.dateAdded, noteDateRange.start, noteDateRange.end))
    ? sourceCurrent
    : null;
  const openCreate = () => {
    if (isNegotiatedCarePlanTab) {
      setCarePlanMode("new");
      return;
    }
    onCreate(config.createDrawerKey || config.drawerKey);
  };
  const openCurrent = () => {
    if (currentRecord?.routePath) {
      navigate(currentRecord.routePath);
      return;
    }
    if (isNegotiatedCarePlanTab) {
      setCarePlanMode("current");
      return;
    }
    onCreate(config.drawerKey);
  };

  if (isNegotiatedCarePlanTab && carePlanMode) {
    const plan = carePlanMode === "current" ? currentRecord : {
      appraisalId: currentRecord?.appraisalId || "ap-new-mj-2024",
      title: "New negotiated campaign",
      summary: "Create a negotiated campaign from vehicle notes, provider inputs, and social worker negotiation.",
      dateAdded: new Date().toISOString(),
      socialWorker: "Amanda Parker",
      status: "Draft",
    };
    const ncpAppraisalId = plan.appraisalId || plan.appraisalProcessId || "ap-new-mj-2024";
    const ncpRecordPath = `/appraisals/${ncpAppraisalId}/records/negotiated-plan`;

    return (
      <Panel
        title={carePlanMode === "current" ? "Current Negotiated Campaign" : "Create Negotiated Campaign"}
        actionNode={(
          <button className="mini-btn previous-plans-button" type="button" onClick={() => setCarePlanMode(null)}>
            <i className="ti ti-arrow-left" /> Previous Plans
          </button>
        )}
      >
        <div className="vehicle-plan-workspace-head">
          <span>{recordBadge(plan)}</span>
          <Link className="mini-btn" to={ncpRecordPath}>
            <i className="ti ti-template" /> 19-page NCP Form
          </Link>
          <Link className="mini-btn" to={`${ncpRecordPath}?view=preview`}>
            <i className="ti ti-eye" /> 19-page Preview
          </Link>
          <Link className="mini-btn" to={`${ncpRecordPath}?view=preview&print=true`}>
            <i className="ti ti-printer" /> Print Preview
          </Link>
        </div>
        <div className="vehicle-plan-workspace">
          <div>
            <label>Plan title</label>
            <strong>{recordTitle(plan)}</strong>
          </div>
          <div>
            <label>Social worker</label>
            <strong>{plan.socialWorker}</strong>
          </div>
          <div>
            <label>Date created</label>
            <strong>{formatDate(plan.dateAdded)}</strong>
          </div>
          <div className="vehicle-plan-workspace-summary">
            <label>Campaign notes</label>
            <p>{plan.summary}</p>
          </div>
        </div>
        <section className="ncp-source-section">
          <div>
            <h3>Data Sources</h3>
            <p>Records used to generate or support this negotiated campaign.</p>
          </div>
          <div className="ncp-source-grid">
            {negotiatedPlanSources.map(([type, title, date, status, icon]) => (
              <article className="ncp-source-card" key={type}>
                <span><i className={`ti ${icon}`} /></span>
                <div>
                  <strong>{type}</strong>
                  <p>{title}</p>
                  <small>{date}</small>
                </div>
                <em>{status}</em>
              </article>
            ))}
          </div>
        </section>
        <form className="ncp-template-form">
          {ncpFormSections.map((section) => (
            <section className="ncp-form-section" key={section.title}>
              <h3>{section.title}</h3>
              <div className="ncp-form-grid">
                {section.fields.map((field) => (
                  <NcpField field={field} key={field[1]} />
                ))}
              </div>
            </section>
          ))}
        </form>
        <div className="vehicle-plan-workspace-actions">
          <button className="btn primary" type="button">Save draft</button>
          <button className="btn secondary" type="button">Send for review</button>
        </div>
      </Panel>
    );
  }

  return (
    <Panel title={title}>
      <div className="vehicle-tab-list-head">
        <p>{intro}</p>
        {showCreate ? (
          <button className="mini-btn" type="button" onClick={openCreate}>
            <i className="ti ti-plus" /> {config.createLabel}
          </button>
        ) : null}
      </div>
      {isDailyNotesTab ? (
        <div className="vehicle-date-range">
          <label>
            <span>From</span>
            <input type="date" value={noteDateRange.start} max={noteDateRange.end} onChange={(event) => setNoteDateRange((current) => ({ ...current, start: event.target.value }))} />
          </label>
          <label>
            <span>To</span>
            <input type="date" value={noteDateRange.end} min={noteDateRange.start} onChange={(event) => setNoteDateRange((current) => ({ ...current, end: event.target.value }))} />
          </label>
        </div>
      ) : null}
      {error ? <p className="form-error">{error}</p> : null}
      {currentRecord ? (
        <button className="vehicle-active-item" type="button" onClick={openCurrent}>
          <span>
            <small>{config.currentLabel}</small>
            <strong>{recordTitle(currentRecord)}</strong>
            <em>{recordSummary(currentRecord)}</em>
          </span>
          <b>{formatDate(currentRecord.dateAdded)}</b>
          <i>{recordBadge(currentRecord)}</i>
        </button>
      ) : null}
      <div className="vehicle-record-section-title">Previous records</div>
      <div className="vehicle-tab-list" style={{ "--vehicle-record-columns": config.gridTemplate }}>
        <div className="vehicle-tab-list-row vehicle-tab-list-head-row">
          {config.columns.map(([label]) => (
            <span key={label}>{label}</span>
          ))}
          <span />
        </div>
        {rows.map((record) => (
          <button
            className="vehicle-tab-list-row"
            type="button"
            onClick={() => (record.routePath ? navigate(record.routePath) : onCreate(config.drawerKey))}
            key={`${tab}-${record.id || recordTitle(record)}-${record.dateAdded}`}
          >
            {config.columns.map((column) => (
              <RecordValue record={record} column={column} key={column[1]} />
            ))}
            <i className="ti ti-chevron-right" />
          </button>
        ))}
        {loading ? <div className="vehicle-inline-empty">Loading records...</div> : null}
        {!loading && !rows.length ? <div className="vehicle-inline-empty">No previous records found.</div> : null}
      </div>
    </Panel>
  );
}

function OnboardingDataTab({ title, intro, sections = [], error = "" }) {
  const visibleSections = sections.filter((section) => section.rows?.length);
  return (
    <Panel title={title}>
      <div className="vehicle-onboarding-data-head">
        <p>{intro}</p>
      </div>
      {error ? <p className="form-error">{error}</p> : null}
      {visibleSections.length ? (
        <div className="vehicle-onboarding-data-stack">
          {visibleSections.map((section) => (
            <article className="vehicle-onboarding-data-card" key={section.title}>
              <div className="vehicle-onboarding-data-card-head">
                <span><i className={`ti ${section.icon || "ti-clipboard-list"}`} /></span>
                <strong>{section.title}</strong>
              </div>
              <dl>
                {section.rows.map(([label, value, action]) => {
                  const renderedValue = onboardingValue(value);
                  return (
                    <div className={hasHtml(renderedValue) ? "is-rich" : ""} key={label}>
                      <dt>{label}</dt>
                      <dd className={renderedValue ? "" : "is-empty"}>
                        <span className="vehicle-onboarding-row-value">
                          {renderedValue && hasHtml(renderedValue) ? (
                            <span dangerouslySetInnerHTML={{ __html: renderedValue }} />
                          ) : (
                            renderedValue || "Not added"
                          )}
                        </span>
                        {action?.type === "document" ? (
                          <span className="vehicle-onboarding-doc-actions">
                            {action.viewUrl ? (
                              <a
                                className="vehicle-doc-icon"
                                href={action.viewUrl}
                                target="_blank"
                                rel="noreferrer"
                                title={action.isImage ? "View image" : "Preview document"}
                              >
                                <i className={`ti ${action.isImage ? "ti-photo" : "ti-eye"}`} />
                              </a>
                            ) : null}
                            {action.downloadUrl ? (
                              <a
                                className="vehicle-doc-icon"
                                download={action.fileName || undefined}
                                href={action.downloadUrl}
                                target="_blank"
                                rel="noreferrer"
                                title="Download document"
                              >
                                <i className="ti ti-download" />
                              </a>
                            ) : null}
                            {!action.viewUrl && !action.downloadUrl ? (
                              <span className="vehicle-doc-icon is-disabled" title="File link unavailable">
                                <i className="ti ti-file-off" />
                              </span>
                            ) : null}
                          </span>
                        ) : null}
                      </dd>
                    </div>
                  );
                })}
              </dl>
            </article>
          ))}
        </div>
      ) : (
        <div className="vehicle-inline-empty">No onboarding data has been captured for this step yet.</div>
      )}
    </Panel>
  );
}

export default function VehicleDetailPage({ publicMode = false }) {
  const { vehicleId, id: legacyVehicleId } = useParams();
  const navigate = useNavigate();
  const id = vehicleId || legacyVehicleId;
  const [activeDrawer, setActiveDrawer] = useState(null);
  const [vehicle, setVehicle] = useState(null);
  const [vehicleLoading, setVehicleLoading] = useState(true);
  const [vehicleError, setVehicleError] = useState("");
  const [assignedStaff, setAssignedStaff] = useState([]);
  const [onboardingPreview, setOnboardingPreview] = useState(null);
  const [onboardingPreviewError, setOnboardingPreviewError] = useState("");
  const [vehicleAppraisalProcesses, setVehicleAppraisalProcesses] = useState([]);
  const [vehicleAppraisalsError, setVehicleAppraisalsError] = useState("");
  const [vehicleDailyNotes, setVehicleDailyNotes] = useState([]);
  const [vehicleDocuments, setVehicleDocuments] = useState([]);
  const [vehicleContacts, setVehicleContacts] = useState([]);
  const [vehicleNegotiatedCarePlans, setVehicleNegotiatedCarePlans] = useState([]);
  const [vehicleAppraisalRecords, setVehicleAppraisalRecords] = useState([]);
  const [vehicleRelatedRecordsError, setVehicleRelatedRecordsError] = useState("");
  const [startingAppraisal, setStartingAppraisal] = useState(false);
  const [vehicleActionLoading, setVehicleActionLoading] = useState("");
  const [inquiryOpen, setInquiryOpen] = useState(false);
  const [inquiryForm, setInquiryForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    zipCode: "",
    phone: "",
    scheduleVisit: "no",
    visitDate: "",
    visitTime: "",
    messageToSeller: "",
  });
  const [inquiryError, setInquiryError] = useState("");
  const [inquirySubmitting, setInquirySubmitting] = useState(false);
  const { siteProfile: fetchedSiteProfile } = useSiteProfile();
  const siteProfile = { ...fallbackSiteProfile, ...fetchedSiteProfile };
  const vehicleName = fullNameFor(vehicle || {});
  const vehicleCareLevel = vehicle?.careLevelLabel || labelize(vehicle?.careLevel || "");
  const vehicleNcpStatus = vehicle?.ncpStatusLabel || labelize(vehicle?.ncpStatus || vehicle?.carePlanStatus || "");
  const vehicleInitials = vehicle?.initials || initialsFor(vehicleName);
  const vehicleStatus = labelize(vehicle?.status || vehicle?.ncpStatus || vehicle?.carePlanStatus || "In Review");
  const vehicleStatusKey = normalizeStatusKey(vehicle?.status);
  const vehicleStatusClass = `is-${(vehicleStatusKey || "IN_REVIEW").toLowerCase().replace(/_/g, "-")}`;
  const showMarkVehicleActive = ["PENDING", "REMOVED"].includes(vehicleStatusKey);
  const vehicleLocation = locationFor(vehicle || {});
  const vehicleContact = contactFor(vehicle || {});
  const vehicleAge = vehicle?.age || ageFromBirthDate(vehicle?.birthDate || vehicle?.dateOfBirth);
  const vehiclePrice = vehicle?.price || vehicle?.askingPrice || vehicle?.listPrice || "";
  const vehicleMileage = vehicle?.mileage || vehicle?.odometer || "";
  const vehiclePriceDisplay = displayPrice(vehiclePrice);
  const vehicleMileageDisplay = displayMileage(vehicleMileage);
  const vehicleDrivetrain = vehicle?.drivetrain || vehicle?.driveType || "";
  const vehicleFuel = vehicle?.fuel || vehicle?.fuelType || "";
  const vehicleRoom = vehicle?.roomNumber || vehicle?.room || "";
  const vehicleNextReview = compactDate(vehicle?.nextReviewDate || vehicle?.nextReview);
  const vehicleAdmissionDate = compactDate(vehicle?.moveInDate || vehicle?.expectedAdmissionDate || vehicle?.admissionDate || vehicle?.movedInDate);
  const vehicleAddress = addressSummaryFor(vehicle || {});
  const vehiclePrimaryContactName = vehicle?.primaryContactName || vehicle?.emergencyContactName || vehicleContact.contactPersonName || "";
  const vehiclePrimaryContactRelationship = vehicle?.primaryContactRelationship || vehicle?.emergencyContactRelationship || vehicleContact.contactPersonTitle || "";
  const vehiclePrimaryContactPhone = primaryPhoneFor(vehicle || {});
  const vehiclePrimaryContactEmail = primaryEmailFor(vehicle || {});
  const activeVehicleAppraisalProcess = pickActiveAppraisalProcess(vehicleAppraisalProcesses);
  const vehicleFlowSource =
    activeVehicleAppraisalProcess?.patientType ||
    vehicle?.flowType ||
    vehicle?.vehicleFlowType ||
    vehicle?.patientType ||
    "new-vehicle";
  const vehicleFlow = String(vehicleFlowSource).toUpperCase() === "NEW" ? "new-vehicle" : String(vehicleFlowSource).toUpperCase() === "RESIDENT" ? "existing-vehicle" : vehicleFlowSource;
  const vehicleAppraisalType = vehicleFlow === "new-vehicle" ? "NEW_RESIDENT_ASSESSMENT" : "ANNUAL_ASSESSMENT";
  const vehicleAppraisalLabel = vehicleAppraisalType === "NEW_RESIDENT_ASSESSMENT" ? "Start new vehicle appraisal" : "Start annual appraisal";
  const vehicleAppraisalDisplayType = vehicleAppraisalType === "NEW_RESIDENT_ASSESSMENT" ? "Initial Appraisal / Onboarding Appraisal" : "Annual Appraisal";
  const vehicleAppraisalStage = vehicleAppraisalType === "NEW_RESIDENT_ASSESSMENT" ? "STATE_INITIAL_ASSESSMENT" : "YEARLY_STATE_ASSESSMENT";
  const vehicleAppraisalStatus = normalizeStatusKey(
    activeVehicleAppraisalProcess?.stage ||
    activeVehicleAppraisalProcess?.workflowStatus ||
    activeVehicleAppraisalProcess?.status ||
    vehicle?.appraisalStatus ||
    vehicle?.appraisalProcessStatus ||
    vehicle?.currentAppraisalStatus ||
    vehicle?.ncpStatus ||
    vehicle?.carePlanStatus
  );
  const activeAppraisalProcessReference =
    activeVehicleAppraisalProcess?.id ||
    activeVehicleAppraisalProcess?.appraisalProcessId ||
    activeVehicleAppraisalProcess?.processId ||
    vehicle?.activeAppraisalProcessId ||
    vehicle?.currentAppraisalProcessId ||
    vehicle?.openAppraisalProcessId;
  const activeAppraisalStatusKeys = ["OPEN", "ACTIVE", "IN_PROGRESS", "STATE_INITIAL_ASSESSMENT", "YEARLY_STATE_ASSESSMENT", "MEET_AND_GREET", "PROVIDER_REVIEW", "THIRTY_DAY_PROBATION", "PRELIMINARY_CARE_PLAN", "NEGOTIATED_CARE_PLAN", "PAYMENT_NEGOTIATION", "FINAL_ASSESSMENT_UPDATE", "STATE_CONFIRMATION"];
  const hasActiveVehicleAppraisalProcess =
    Boolean(activeVehicleAppraisalProcess || activeAppraisalProcessReference) ||
    activeAppraisalStatusKeys.includes(vehicleAppraisalStatus);
  const canStartVehicleAppraisal = !vehicleLoading && vehicleAppraisalStatus === "NOT_STARTED" && !hasActiveVehicleAppraisalProcess;
  const canCreateAnnualAppraisal = !vehicleLoading && vehicleStatusKey === "ACTIVE" && !hasActiveVehicleAppraisalProcess;
  const vehicleFlowStages = carePlanFlowStages.filter((stage) => stage.appliesTo.includes(vehicleFlow));
  const actualFlowStageKey =
    activeVehicleAppraisalProcess?.stage ||
    activeVehicleAppraisalProcess?.workflowStage ||
    activeVehicleAppraisalProcess?.currentStage ||
    (activeVehicleAppraisalProcess ? vehicleAppraisalStage : "") ||
    vehicle?.appraisalStage ||
    vehicle?.appraisalProcessStage ||
    vehicle?.currentAppraisalStage ||
    vehicle?.ncpStage ||
    vehicle?.ncpStatus ||
    vehicle?.carePlanStatus ||
    "";
  const actualFlowStageIndex = stageIndexFor(vehicleFlowStages, actualFlowStageKey);
  const vehicleFlowStagesWithStatus = vehicleFlowStages.map((stage, index) => {
    const status = actualFlowStageIndex < 0 ? "Pending" : index < actualFlowStageIndex ? "Complete" : index === actualFlowStageIndex ? "Current" : "Pending";
    return {
      ...stage,
      displayLabel: vehicleFlow === "existing-vehicle" && stage.existingLabel ? stage.existingLabel : stage.label,
      displayText: vehicleFlow === "existing-vehicle" && stage.existingText ? stage.existingText : stage.text,
      status,
    };
  });
  const currentAssignedStaff = assignedStaff.length ? assignedStaff : [];
  const activeStaff = currentAssignedStaff.find((staff) => String(staff.key || staff.id) === String(activeDrawer));
  const submitVehicleAppraisalStart = async () => {
    if (!id || startingAppraisal) return;
    setStartingAppraisal(true);
    try {
      const payload = {
        appraisalType: vehicleAppraisalType,
        patientType: vehicleAppraisalType === "NEW_RESIDENT_ASSESSMENT" ? "NEW" : "RESIDENT",
        vehicleId: id,
        vehicleName,
        startDate: new Date().toISOString().slice(0, 10),
        status: "OPEN",
        stage: vehicleAppraisalStage,
      };
      const created = await careService.appraisalProcesses.create(payload);
      const createdId = created?.id || created?.appraisalProcessId || created?.processId;
      setVehicleAppraisalProcesses((processes) => [created || { ...payload, id: createdId }, ...processes]);
      message.success(`${vehicleAppraisalDisplayType} started for ${vehicleName}.`);
      if (createdId) navigate(`/appraisals/${createdId}/records/initial-appraisal`);
    } catch (err) {
      message.error(err?.response?.data?.message || err?.message || "Unable to start appraisal process.");
    } finally {
      setStartingAppraisal(false);
    }
  };
  const submitAnnualAppraisalProcessStart = async () => {
    if (!id || startingAppraisal) return;
    setStartingAppraisal(true);
    try {
      const payload = {
        appraisalType: "ANNUAL_ASSESSMENT",
        patientType: "RESIDENT",
        vehicleId: id,
        vehicleName,
        startDate: new Date().toISOString().slice(0, 10),
        status: "OPEN",
        stage: "YEARLY_STATE_ASSESSMENT",
      };
      const created = await careService.appraisalProcesses.create(payload);
      const createdId = created?.id || created?.appraisalProcessId || created?.processId;
      setVehicleAppraisalProcesses((processes) => [created || { ...payload, id: createdId }, ...processes]);
      message.success(`Annual appraisal process started for ${vehicleName}.`);
      if (createdId) navigate(`/appraisals/${createdId}/new`);
    } catch (err) {
      message.error(err?.response?.data?.message || err?.message || "Unable to create annual appraisal process.");
    } finally {
      setStartingAppraisal(false);
    }
  };
  const startVehicleAppraisal = () => {
    if (!id || startingAppraisal) return;
    Modal.confirm({
      title: `Start ${vehicleAppraisalDisplayType}?`,
      content: `Would you like to confirm initiating the ${vehicleAppraisalDisplayType} for ${vehicleName}? Once confirmed, you will move to the State Initial Appraisal step.`,
      okText: "Confirm and start",
      cancelText: "Cancel",
      centered: true,
      onOk: submitVehicleAppraisalStart,
    });
  };
  const startAnnualAppraisalProcess = () => {
    if (!id || startingAppraisal) return;
    Modal.confirm({
      title: "Create annual appraisal process?",
      content: `Would you like to confirm initiating the Annual Appraisal process for ${vehicleName}? Once confirmed, the process will move to the Yearly State Appraisal step.`,
      okText: "Confirm and start",
      cancelText: "Cancel",
      centered: true,
      onOk: submitAnnualAppraisalProcessStart,
    });
  };
  const vehiclePersonaDrawer = {
    title: "Vehicle Persona",
    intro: "Vehicle bio data, contact information, next of kin details, and dealer profile from the vehicle record.",
    sections: [
      {
        title: "Bio Data",
        rows: [
          ["Full name", displayValue(vehicleName)],
          ["Preferred name", displayValue(vehicle?.preferredName)],
          ["Date of birth", displayValue(compactDate(vehicle?.birthDate || vehicle?.dateOfBirth))],
          ["Age", displayValue(vehicleAge)],
          ["Gender", displayValue(labelize(vehicle?.gender || vehicle?.sex))],
          ["Primary language", displayValue(vehicle?.primaryLanguage)],
          ["Room", displayValue(vehicleRoom)],
          ["Status", displayValue(vehicleStatus)],
          ["Admission date", displayValue(vehicleAdmissionDate)],
          ["Discharge date", displayValue(compactDate(vehicle?.dischargeDate))],
        ],
      },
      {
        title: "Contact Information",
        rows: [
          ["Phone", displayValue(vehiclePrimaryContactPhone || vehicleContact.primaryPhone)],
          ["Secondary phone", displayValue(vehicleContact.secondaryPhone)],
          ["Email", displayValue(vehiclePrimaryContactEmail || vehicleContact.primaryEmail)],
          ["Secondary email", displayValue(vehicleContact.secondaryEmail)],
          ["Address", displayValue(vehicleAddress)],
          ["City", displayValue(vehicleLocation.city)],
          ["County", displayValue(vehicleLocation.county)],
          ["State", displayValue(vehicleLocation.state)],
          ["Zip Code", displayValue(vehicleLocation.zipCode || vehicleLocation.zip)],
        ],
      },
      {
        title: "Primary Contact / Next of Kin",
        rows: [
          ["Name", displayValue(vehiclePrimaryContactName)],
          ["Relationship", displayValue(vehiclePrimaryContactRelationship)],
          ["Phone", displayValue(vehiclePrimaryContactPhone)],
          ["Email", displayValue(vehiclePrimaryContactEmail)],
          ["PoA / guardian notes", displayValue(vehicle?.poaGuardianNotes)],
        ],
      },
      {
        title: "Dealer Profile",
        rows: [
          ["Vehicle level", displayValue(vehicleCareLevel)],
          ["NCP status", displayValue(vehicleNcpStatus)],
          ["Next review", displayValue(vehicleNextReview)],
          ["Primary provider", displayValue(vehicle?.primaryProvider)],
          ["Pharmacy", displayValue(vehicle?.pharmacy)],
          ["Diagnosis", displayValue(vehicle?.diagnosis)],
          ["Medication notes", displayValue(vehicle?.medicationNotes)],
          ["Vehicle needs", displayValue(vehicle?.careNeeded)],
          ["Behavior and safety notes", displayValue(vehicle?.behaviorSafetyNotes)],
          ["General notes", displayValue(vehicle?.notes)],
        ],
      },
    ],
    action: "Edit persona",
  };
  const drawer = activeStaff
    ? {
        title: activeStaff.name || activeStaff.staffName || "Assigned staff",
        intro: `${activeStaff.role || activeStaff.assignmentRole || "Dealer staff"} assigned to ${vehicleName} today.`,
        sections: [
          {
            title: "Staff Details",
            rows: [
              ["Name", activeStaff.name || activeStaff.staffName || "Not set"],
              ["Role", activeStaff.role || activeStaff.assignmentRole || "Not set"],
              ["Shift", activeStaff.shift || [activeStaff.from, activeStaff.to].filter(Boolean).join(" - ") || "Not set"],
              ["Assignment", activeStaff.assignment || activeStaff.description || "Not set"],
            ],
          },
          {
            title: "Contact Information",
            rows: [
              ["Phone", activeStaff.phone || "Not set"],
              ["Email", activeStaff.email || "Not set"],
            ],
          },
        ],
        contactActions: [
          ["Chat", "ti-message-circle", "#chat"],
          ["Call", "ti-phone", activeStaff.phone ? `tel:${String(activeStaff.phone).replace(/[^+\d]/g, "")}` : "#"],
        ],
      }
    : activeDrawer
      ? activeDrawer === "persona" ? vehiclePersonaDrawer : drawerContent[activeDrawer]
      : null;
  const snapshot = [
    ["Price", vehiclePrice || "Not set"],
    ["Mileage", vehicleMileage || "Not set"],
    ["Drivetrain", vehicleDrivetrain || "Not set"],
    ["Fuel", vehicleFuel || "Not set"],
    ["Color", vehicle?.color || "Not set"],
    ["Inventory status", vehicle?.inventoryStatus || vehicleStatus || "Not set"],
    ["Sales contact", vehiclePrimaryContactName || "Not set"],
  ];
  const vehicleActionSections = vehicleSections.map(([key, icon, title, text, status, createdAt]) => {
    if (key === "notes") {
      return [key, icon, title, vehicle?.lastNoteSummary || text, vehicle?.lastNoteAt ? `Last note ${compactDate(vehicle.lastNoteAt)}` : status, vehicle?.lastNoteAt || createdAt];
    }
    if (key === "carePlan") {
      return [key, icon, title, vehicle?.careNeeded || text, vehicleNcpStatus || status, vehicle?.updatedAt || createdAt];
    }
    if (key === "documents") {
      return [key, icon, title, vehicle?.stateAppraisalFileName ? `State appraisal: ${vehicle.stateAppraisalFileName}` : text, vehicle?.stateAppraisalImported ? "Imported" : status, vehicle?.updatedAt || createdAt];
    }
    if (key === "alerts") {
      return [key, icon, title, text, `${Number(vehicle?.openAlertCount || 0)} open`, vehicle?.updatedAt || createdAt];
    }
    if (key === "appraisals") {
      return [key, icon, title, vehicle?.intakeSource ? `Intake source: ${vehicle.intakeSource}` : text, vehicle?.stateAppraisalImported ? "Appraisal imported" : status, vehicle?.updatedAt || createdAt];
    }
    if (key === "team") {
      return [key, icon, title, vehicle?.careTeamSummary || text, currentAssignedStaff.length ? `${currentAssignedStaff.length} assigned today` : status, vehicle?.updatedAt || createdAt];
    }
    return [key, icon, title, text, status, createdAt];
  });
  const onboardingRoot = onboardingPreview?.previewPayload || onboardingPreview?.payload || onboardingPreview || {};
  const onboardingStages =
    onboardingRoot.onboardingStages ||
    onboardingRoot.previewPayload?.onboardingStages ||
    onboardingRoot.payload?.onboardingStages ||
    {};
  const bioStage = onboardingStages.bio || onboardingRoot.bio || {};
  const contactsStage = onboardingStages.contacts || onboardingRoot.contacts || {};
  const careHistoryStage = onboardingStages.careHistory || onboardingRoot.careHistory || {};
  const careSnapshotStage = onboardingStages.careSnapshot || onboardingStages.care || onboardingRoot.careSnapshot || onboardingRoot.care || {};
  const documentsStage = onboardingStages.documents || onboardingRoot.documents || {};
  const onboardingBio = bioStage.vehicle || bioStage.payload?.vehicle || bioStage.payload || bioStage || {};
  const onboardingBioSourceForm = onboardingBio.sourceForm || {};
  const onboardingBioFormData = onboardingBioSourceForm.formData || {};
  const vehicleCoverage = {
    codeStatus: vehicle?.codeStatus,
    advancedDirectives: vehicle?.advancedDirectives,
    medicareNumber: vehicle?.medicareNumber,
    medicaidNumber: vehicle?.medicalRecordNumber,
    supplementaryInsurances: vehicle?.supplementaryInsurances,
  };
  const vehicleBioAddress = {
    addressLine1: vehicleLocation.addressLine1 || vehicleLocation.address,
    addressLine2: vehicleLocation.addressLine2,
    city: vehicleLocation.city,
    county: vehicleLocation.county,
    state: vehicleLocation.state,
    country: vehicleLocation.country,
    zipCode: vehicleLocation.zipCode || vehicleLocation.zip,
  };
  const onboardingContacts = contactsStage.contacts || contactsStage.payload?.contacts || onboardingBio.contacts || [];
  const onboardingCareHistory = careHistoryStage.careHistory || careHistoryStage.payload?.careHistory || onboardingBio.careHistory || {};
  const onboardingCareSnapshot = careSnapshotStage.initialCareSnapshot || careSnapshotStage.payload?.initialCareSnapshot || onboardingBio.initialCareSnapshot || {};
  const onboardingDocuments = documentsStage.documents || documentsStage.payload?.documents || onboardingBio.documents || {};
  const bioPersonal = onboardingBio.personalInformation || {};
  const bioCoverage = onboardingBio.healthAndCoverage || vehicleCoverage;
  const onboardingBioSections = [
    {
      title: "Personal Information",
      icon: "ti-user-circle",
      rows: [
        ["Full name", [bioPersonal.firstName || onboardingBioFormData.firstName || vehicle?.firstName, bioPersonal.middleName || onboardingBioFormData.middleName || vehicle?.middleName, bioPersonal.lastName || onboardingBioFormData.lastName || vehicle?.lastName].filter(Boolean).join(" ") || vehicleName],
        ["Preferred name", bioPersonal.preferredName || onboardingBioFormData.preferredName || vehicle?.preferredName],
        ["Birth date", compactDate(bioPersonal.birthDate || onboardingBioFormData.dateOfBirth || vehicle?.birthDate || vehicle?.dateOfBirth)],
        ["Sex", labelize(bioPersonal.sex || onboardingBioFormData.sex || vehicle?.gender)],
        ["Social Security Number", bioPersonal.socialSecurityNumber || onboardingBioFormData.socialSecurityNumber],
        ["Primary language", bioPersonal.primaryLanguage || onboardingBioFormData.primaryLanguage || vehicle?.primaryLanguage],
      ],
    },
    {
      title: "Address",
      icon: "ti-map-pin",
      rows: [
        ["Address", addressSummary(onboardingBio.address) || addressSummary(vehicleBioAddress) || vehicleAddress],
        ["City", onboardingBio.address?.city || onboardingBioFormData.city || vehicleLocation.city],
        ["County", onboardingBio.address?.county || onboardingBioFormData.county || vehicleLocation.county],
        ["State", onboardingBio.address?.state || onboardingBioFormData.state || vehicleLocation.state],
        ["Country", onboardingBio.address?.country || onboardingBioFormData.country || vehicleLocation.country],
        ["Zip Code", onboardingBio.address?.zipCode || onboardingBio.address?.zip || onboardingBioFormData.zip || vehicleLocation.zipCode],
      ],
    },
    {
      title: "Coverage and Notes",
      icon: "ti-shield-check",
      rows: [
        ["Code status", bioCoverage.codeStatus || onboardingBioFormData.codeStatus],
        ["Advanced directives", bioCoverage.advancedDirectives ?? onboardingBioFormData.advancedDirectives],
        ["Reference Number", bioCoverage.medicareNumber || onboardingBioFormData.medicareNumber],
        ["Medicaid Number", bioCoverage.medicaidNumber || onboardingBioFormData.medicaidNumber],
        ["Supplementary insurances", bioCoverage.supplementaryInsurances || onboardingBioSourceForm.otherInsurances],
        ["Background notes", onboardingBio.backgroundNotes || onboardingBio.notes || onboardingBioFormData.bioNotes || vehicle?.notes],
      ],
    },
  ];
  const fallbackContacts = vehiclePrimaryContactName ? [{
    relationship: vehiclePrimaryContactRelationship || "Primary contact",
    firstName: vehiclePrimaryContactName,
    email: vehiclePrimaryContactEmail,
    phones: { preferred: vehiclePrimaryContactPhone },
    primaryContact: true,
  }] : [];
  const onboardingContactSections = (onboardingContacts.length ? onboardingContacts : fallbackContacts).map((contact, index) => ({
    title: `${contact.relationship || contact.contactType || "Contact"} ${index + 1}`,
    icon: "ti-address-book",
    rows: [
      ["Name", [contact.firstName, contact.middleName, contact.lastName].filter(Boolean).join(" ") || contact.organizationName],
      ["Relationship", contact.relationship],
      ["Contact type", labelize(contact.contactType)],
      ["Email", contact.email],
      ["Phones", phonesSummary(contact.phones)],
      ["Address", addressSummary(contact.address)],
      ["Primary contact", contact.primaryContact],
      ["Emergency contact", contact.emergencyContact],
      ["Decision maker", contact.decisionMaker],
      ["Next of kin", contact.nextOfKin],
      ["Notes", contact.notes],
    ],
  }));
  const onboardingCareHistorySections = [
    {
      title: "Admission Source",
      icon: "ti-building-hospital",
      rows: [
        ["Admitted at hospital", onboardingCareHistory.admittedAtHospital],
        ["Hospital", onboardingCareHistory.hospitalAdmission?.name || onboardingBioFormData.hospitalName],
        ["Hospital contact", onboardingCareHistory.hospitalAdmission?.contactName || onboardingBioFormData.hospitalContactName],
        ["Hospital phone", phonesSummary(onboardingCareHistory.hospitalAdmission?.phones) || onboardingBioFormData.hospitalPhone],
        ["Hospital email", onboardingCareHistory.hospitalAdmission?.email || onboardingBioFormData.hospitalEmail],
        ["Hospital address", addressSummary(onboardingCareHistory.hospitalAdmission?.address) || addressSummary(locationFor({ locationInformation: {
          addressLine1: onboardingBioFormData.hospitalAddress,
          city: onboardingBioFormData.hospitalCity,
          county: onboardingBioFormData.hospitalCounty,
          state: onboardingBioFormData.hospitalState,
          country: onboardingBioFormData.hospitalCountry,
          zipCode: onboardingBioFormData.hospitalZip,
        } }))],
        ["Hospital notes", onboardingCareHistory.hospitalAdmission?.notes || onboardingBioFormData.admissionDescription],
      ],
    },
    {
      title: "Previous Family Home",
      icon: "ti-home-heart",
      rows: [
        ["Coming from another family home", onboardingCareHistory.comingFromAnotherFamilyHome],
        ["Family home name", onboardingCareHistory.previousFamilyHome?.name],
        ["Contact name", onboardingCareHistory.previousFamilyHome?.contactName],
        ["Phone", phonesSummary(onboardingCareHistory.previousFamilyHome?.phones)],
        ["Email", onboardingCareHistory.previousFamilyHome?.email],
        ["Address", addressSummary(onboardingCareHistory.previousFamilyHome?.address)],
        ["Length of stay", [onboardingCareHistory.previousFamilyHome?.years ? `${onboardingCareHistory.previousFamilyHome.years} years` : "", onboardingCareHistory.previousFamilyHome?.months ? `${onboardingCareHistory.previousFamilyHome.months} months` : ""].filter(Boolean).join(", ")],
        ["Notes", onboardingCareHistory.previousFamilyHome?.notes],
      ],
    },
    {
      title: "Prior Sales Consultant",
      icon: "ti-user-heart",
      rows: [
        ["Had prior sales consultant", onboardingCareHistory.hadPriorCaregiver],
        ["Sales consultant name", [onboardingCareHistory.priorCaregiver?.firstName, onboardingCareHistory.priorCaregiver?.middleName, onboardingCareHistory.priorCaregiver?.lastName].filter(Boolean).join(" ")],
        ["Relationship", onboardingCareHistory.priorCaregiver?.relationship],
        ["Phone", phonesSummary(onboardingCareHistory.priorCaregiver?.phones)],
        ["Email", onboardingCareHistory.priorCaregiver?.email],
        ["Address", addressSummary(onboardingCareHistory.priorCaregiver?.address)],
        ["Sales details", onboardingCareHistory.priorCaregiver?.notes],
      ],
    },
    {
      title: "Vehicle History Notes",
      icon: "ti-notes",
      rows: [["Notes", onboardingCareHistory.notes]],
    },
  ];
  const onboardingCareSnapshotSections = [
    {
      title: "Initial Vehicle Snapshot",
      icon: "ti-heart-rate-monitor",
      rows: [
        ["Condition summary", onboardingCareSnapshot.conditionSummary || onboardingBioFormData.admissionDescription],
        ["Diagnosis", onboardingCareSnapshot.diagnosis || onboardingBioFormData.diagnosis || vehicle?.diagnosis],
        ["Allergies", onboardingCareSnapshot.allergies || onboardingBioFormData.allergies],
        ["Medication notes", onboardingCareSnapshot.medicationNotes || onboardingBioFormData.medicationNotes || vehicle?.medicationNotes],
        ["Vehicle needs", onboardingCareSnapshot.careNeeded || onboardingBioFormData.careNeeded || vehicle?.careNeeded],
        ["Behavior and safety notes", onboardingCareSnapshot.behaviorSafetyNotes || onboardingBioFormData.behaviorSafetyNotes || vehicle?.behaviorSafetyNotes],
      ],
    },
    {
      title: "Custom Questions",
      icon: "ti-question-mark",
      rows: (onboardingCareSnapshot.customQuestions || []).map((question, index) => [
        question.question || `Question ${index + 1}`,
        question.answer || question.response,
      ]),
    },
  ];
  const onboardingDocumentSections = [
    {
      title: "Uploaded Documents",
      icon: "ti-file-upload",
      rows: (onboardingDocuments.uploadedDocuments || onboardingPreview?.uploadedDocuments || (vehicle?.stateAppraisalFileName ? [{ name: vehicle.stateAppraisalFileName, fileName: vehicle.stateAppraisalFileName, description: "State appraisal file" }] : [])).map((document, index) => {
        const fileName = document.fileName || document.name || `Document ${index + 1}`;
        const fileUrl = onboardingDocumentUrlFor(document);
        return [
          document.name || document.fileName || `Document ${index + 1}`,
          onboardingDocumentMetaFor(document),
          {
            type: "document",
            fileName,
            viewUrl: fileUrl,
            downloadUrl: document.downloadUrl || fileUrl,
            isImage: isImageOnboardingDocument(document),
          },
        ];
      }),
    },
    {
      title: "Signed Document Validation",
      icon: "ti-signature",
      rows: (onboardingDocuments.signedDocumentValidations || []).map((document) => [
        document.documentName || "Signed document",
        `Signed verified: ${document.signedVerified ? "Yes" : "No"} | File uploaded: ${document.fileUploaded ? "Yes" : "No"}${document.notes ? ` | Notes: ${document.notes}` : ""}`,
      ]),
    },
  ];
  const mappedVehicleAppraisals = dedupeVehicleAppraisals(
    vehicleAppraisalProcesses.map((process) => ({ ...process, __appraisalProcessSource: true })),
  )
    .sort((a, b) => vehicleAppraisalDateValue(b) - vehicleAppraisalDateValue(a))
    .map(mapVehicleAppraisalRecord);
  const vehicleAppraisalSplit = splitCurrentAndPrevious(mappedVehicleAppraisals, isClosedVehicleAppraisal);
  const currentVehicleAppraisal = vehicleAppraisalSplit.current;
  const previousVehicleAppraisals = vehicleAppraisalSplit.previous;
  const hasCurrentActiveAppraisal = mappedVehicleAppraisals.some(isActiveVehicleAppraisal);
  const showStartNewAppraisal = !vehicleLoading && !hasCurrentActiveAppraisal;
  const mappedDailyNotes = vehicleDailyNotes.map(mapDailyNoteRecord);
  const currentDailyNote = splitCurrentAndPrevious(mappedDailyNotes).current;
  const previousDailyNotes = splitCurrentAndPrevious(mappedDailyNotes).previous;
  const onboardingUploadedDocuments = asList(onboardingDocuments.uploadedDocuments || onboardingPreview?.uploadedDocuments)
    .map((document) => ({
      ...document,
      title: firstText(document.title, document.name, document.fileName, "Onboarding document"),
      summary: onboardingDocumentMetaFor(document) || firstText(document.description, "Onboarding document"),
      dateAdded: recordDate(document) || vehicleAdmissionDate || new Date().toISOString(),
      addedBy: addedByFor(document),
      status: document.confirmed ? "Confirmed" : labelize(firstText(document.storageStatus, document.status, "Uploaded")),
    }));
  const mappedDocuments = [
    ...vehicleDocuments.map(mapDocumentRecord),
    ...onboardingUploadedDocuments,
    ...(vehicle?.stateAppraisalFileName ? [mapDocumentRecord({
      title: vehicle.stateAppraisalFileName,
      fileName: vehicle.stateAppraisalFileName,
      description: "State appraisal file",
      createdAt: vehicle.updatedAt || vehicle.createdAt,
      status: vehicle.stateAppraisalImported ? "Imported" : "Available",
    })] : []),
  ];
  const currentDocument = splitCurrentAndPrevious(mappedDocuments).current;
  const previousDocuments = splitCurrentAndPrevious(mappedDocuments).previous;
  const mappedCareTeam = [
    ...vehicleContacts.map(mapCareTeamRecord),
    ...currentAssignedStaff.map(mapCareTeamRecord),
    ...(vehiclePrimaryContactName ? [mapCareTeamRecord({
      name: vehiclePrimaryContactName,
      relationship: vehiclePrimaryContactRelationship || "Primary contact",
      phone: vehiclePrimaryContactPhone,
      email: vehiclePrimaryContactEmail,
      createdAt: vehicle.createdAt || vehicle.updatedAt,
      summary: [vehiclePrimaryContactPhone, vehiclePrimaryContactEmail].filter(Boolean).join(" | "),
    })] : []),
  ];
  const currentCareTeamMember = splitCurrentAndPrevious(mappedCareTeam).current;
  const previousCareTeamMembers = splitCurrentAndPrevious(mappedCareTeam).previous;
  const mappedNegotiatedCarePlans = vehicleNegotiatedCarePlans.map(mapCarePlanRecord);
  const currentNegotiatedCarePlan = splitCurrentAndPrevious(mappedNegotiatedCarePlans, (plan) =>
    ["COMPLETED", "COMPLETE", "CLOSED", "ARCHIVED"].includes(normalizeStatusKey(plan.status))
  ).current;
  const previousNegotiatedCarePlans = splitCurrentAndPrevious(mappedNegotiatedCarePlans, (plan) =>
    ["COMPLETED", "COMPLETE", "CLOSED", "ARCHIVED"].includes(normalizeStatusKey(plan.status))
  ).previous;
  const mappedPreliminaryPlans = vehicleAppraisalRecords
    .filter(isPreliminaryPlanRecord)
    .map(mapPreliminaryPlanRecord);
  const currentPreliminaryPlan = splitCurrentAndPrevious(mappedPreliminaryPlans).current;
  const previousPreliminaryPlans = splitCurrentAndPrevious(mappedPreliminaryPlans).previous;
  const vehicleHubFacts = [
    ["Price", vehiclePrice || "Not set", "ti-currency-dollar"],
    ["Mileage", vehicleMileage || "Not set", "ti-road"],
    ["Drivetrain", vehicleDrivetrain || "Not set", "ti-steering-wheel"],
    ["Fuel", vehicleFuel || "Not set", "ti-gas-station"],
  ];
  const vehicleHubStats = [
    ["Assigned Today", currentAssignedStaff.length || 0, currentAssignedStaff.length ? "Active coverage" : "Needs assignment", "ti-users-group"],
    ["Appraisals", mappedVehicleAppraisals.length || vehicleAppraisalProcesses.length || 0, currentVehicleAppraisal ? `${recordBadge(currentVehicleAppraisal)} active` : "No active process", "ti-clipboard-text"],
    ["Campaign", vehicleNcpStatus || "Not set", vehicleCareLevel || "Vehicle details not set", "ti-speakerphone"],
    ["Open Alerts", Number(vehicle?.openAlertCount || 0), Number(vehicle?.openAlertCount || 0) ? "Needs follow-up" : "No open alerts", "ti-alert-triangle"],
  ];
  const vehicleListingDetails = vehicle?.listingDetails || listingDetailsFor(vehicle || {});
  const primaryContactDisplay = vehiclePrimaryContactName || "Not set";
  const assignedStaffPreview = currentAssignedStaff.slice(0, 4);
  const vehiclePhotos = vehiclePhotoUrls(vehicle);
  const primaryVehiclePhoto = vehiclePhotos[0] || vehiclePhotoUrl(vehicle?.mainPhoto);
  const dealerName = firstText(siteProfile.name, fallbackSiteProfile.name);
  const dealerPhoneDigits = String(firstText(siteProfile.phone, fallbackSiteProfile.phone)).replace(/\D+/g, "");
  const logoCandidate = firstText(siteProfile.logoUrl, siteProfile.logoSignedUrl, siteProfile.logo, fallbackSiteProfile.logo);
  const dealerLogo = /^(https?:|\/|data:image|blob:)/i.test(String(logoCandidate || ""))
    ? logoCandidate
    : "";
  const faviconCandidate = firstText(siteProfile.faviconUrl, siteProfile.faviconSignedUrl, siteProfile.favicon);
  const dealerFavicon = /^(https?:|\/|data:image|blob:)/i.test(String(faviconCandidate || ""))
    ? faviconCandidate
    : "";
  const vehicleSeoDescription = vehicle
    ? [
      vehicleName,
      vehiclePrice ? `priced at ${vehiclePrice}` : "",
      vehicleMileage ? `with ${vehicleMileage}` : "",
      vehicleDrivetrain || vehicleFuel ? [vehicleDrivetrain, vehicleFuel].filter(Boolean).join(" ") : "",
      `available at ${dealerName}.`,
    ].filter(Boolean).join(" ")
    : `View vehicle details, photos, price, mileage, and availability at ${dealerName}.`;
  const vehicleStructuredData = useMemo(() => {
    if (!publicMode || !vehicle) return [];
    const priceValue = Number(String(vehiclePrice || "").replace(/[^\d.]/g, ""));
    const mileageValue = Number(String(vehicleMileage || "").replace(/[^\d.]/g, ""));
    const canonicalPath = `/inventory/${id}`;
    return [
      {
        "@context": "https://schema.org",
        "@type": "Vehicle",
        name: vehicleName,
        image: primaryVehiclePhoto ? absoluteUrl(primaryVehiclePhoto) : undefined,
        url: absoluteUrl(canonicalPath),
        brand: vehicle?.make || undefined,
        model: vehicle?.model || undefined,
        vehicleModelDate: vehicle?.year || undefined,
        color: vehicle?.exteriorColor || vehicle?.color || undefined,
        fuelType: vehicleFuel || undefined,
        vehicleTransmission: vehicle?.transmission || undefined,
        driveWheelConfiguration: vehicleDrivetrain || undefined,
        mileageFromOdometer: Number.isFinite(mileageValue) && mileageValue > 0 ? {
          "@type": "QuantitativeValue",
          value: mileageValue,
          unitCode: "SMI",
        } : undefined,
        offers: Number.isFinite(priceValue) && priceValue > 0 ? {
          "@type": "Offer",
          price: priceValue,
          priceCurrency: "USD",
          availability: "https://schema.org/InStock",
          url: absoluteUrl(canonicalPath),
          seller: {
            "@type": "AutoDealer",
            "@id": absoluteUrl("/#dealership"),
            name: dealerName,
            telephone: dealerPhoneDigits ? `+1${dealerPhoneDigits}` : undefined,
          },
        } : undefined,
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          {
            "@type": "ListItem",
            position: 1,
            name: "Inventory",
            item: absoluteUrl("/#inventory"),
          },
          {
            "@type": "ListItem",
            position: 2,
            name: vehicleName,
            item: absoluteUrl(canonicalPath),
          },
        ],
      },
    ];
  }, [dealerName, dealerPhoneDigits, id, primaryVehiclePhoto, publicMode, vehicle, vehicleDrivetrain, vehicleFuel, vehicleMileage, vehicleName, vehiclePrice]);
  const resetInquiryForm = () => {
    setInquiryForm({
      firstName: "",
      lastName: "",
      email: "",
      zipCode: "",
      phone: "",
      scheduleVisit: "no",
      visitDate: "",
      visitTime: "",
      messageToSeller: "",
    });
    setInquiryError("");
  };
  const openVehicleInquiry = () => {
    resetInquiryForm();
    setInquiryOpen(true);
  };
  const closeVehicleInquiry = () => {
    if (inquirySubmitting) return;
    setInquiryOpen(false);
    resetInquiryForm();
  };
  const updateInquiryField = (field, value) => {
    setInquiryForm((current) => ({ ...current, [field]: value }));
  };
  const submitVehicleInquiry = async (event) => {
    event.preventDefault();
    if (!id) return;
    const payload = {
      firstName: inquiryForm.firstName.trim(),
      lastName: inquiryForm.lastName.trim(),
      email: inquiryForm.email.trim(),
      zipCode: inquiryForm.zipCode.trim(),
      phone: inquiryForm.phone.trim(),
      scheduledVisitAt: scheduledVisitAtFromForm(inquiryForm),
      messageToSeller: inquiryForm.messageToSeller.trim(),
      vehicleId: id,
    };
    if (!payload.firstName || !payload.lastName || !payload.email || !payload.zipCode) {
      setInquiryError("First name, last name, email, and ZIP code are required.");
      return;
    }
    if (inquiryForm.scheduleVisit === "yes" && !payload.scheduledVisitAt) {
      setInquiryError("Choose a visit date and time.");
      return;
    }
    setInquirySubmitting(true);
    setInquiryError("");
    try {
      await careService.vehicles.createInquiryPublic(id, payload);
      message.success("Thanks. Your vehicle inquiry was sent.");
      setInquiryOpen(false);
      resetInquiryForm();
    } catch (err) {
      setInquiryError(err?.response?.data?.message || err?.message || "Unable to send your inquiry right now.");
    } finally {
      setInquirySubmitting(false);
    }
  };

  const patchVehicleStatus = async (nextStatus, successMessage) => {
    if (!id || vehicleActionLoading) return;
    setVehicleActionLoading(nextStatus);
    try {
      const updated = await careService.vehicles.patch(id, {
        status: nextStatus,
        inventoryStatus: nextStatus,
      });
      setVehicle((current) => ({
        ...(current || {}),
        ...(updated || {}),
        status: updated?.status || nextStatus,
        inventoryStatus: updated?.inventoryStatus || nextStatus,
      }));
      message.success(successMessage);
    } catch (err) {
      message.error(err?.response?.data?.message || err?.message || "Unable to update vehicle.");
    } finally {
      setVehicleActionLoading("");
    }
  };

  const deleteVehicle = async () => {
    if (!id || vehicleActionLoading) return;
    setVehicleActionLoading("delete");
    try {
      await careService.vehicles.delete(id);
      message.success(`${vehicleName} deleted.`);
      navigate("/vehicles");
    } catch (err) {
      message.error(err?.response?.data?.message || err?.message || "Unable to delete vehicle.");
    } finally {
      setVehicleActionLoading("");
    }
  };

  const handleVehicleManagementAction = (action) => {
    if (action === "update") {
      navigate(`/vehicles/${id}/update`);
      return;
    }
    if (action === "sold") {
      Modal.confirm({
        title: "Mark vehicle as sold?",
        content: `${vehicleName} will be marked as sold and removed from active availability.`,
        okText: "Mark Sold",
        cancelText: "Cancel",
        centered: true,
        onOk: () => patchVehicleStatus("SOLD", `${vehicleName} marked as sold.`),
      });
      return;
    }
    if (action === "pending") {
      Modal.confirm({
        title: "Mark vehicle as pending?",
        content: `${vehicleName} will be marked as pending for review or preparation.`,
        okText: "Mark Pending",
        cancelText: "Cancel",
        centered: true,
        onOk: () => patchVehicleStatus("PENDING", `${vehicleName} marked as pending.`),
      });
      return;
    }
    if (action === "active") {
      Modal.confirm({
        title: "Mark vehicle as active?",
        content: `${vehicleName} will be marked as ready and available in inventory.`,
        okText: "Mark Active",
        cancelText: "Cancel",
        centered: true,
        onOk: () => patchVehicleStatus("READY", `${vehicleName} marked as active.`),
      });
      return;
    }
    if (action === "remove") {
      Modal.confirm({
        title: "Remove vehicle from inventory?",
        content: `${vehicleName} will be marked as removed and no longer treated as active inventory.`,
        okText: "Remove Vehicle",
        cancelText: "Cancel",
        centered: true,
        onOk: () => patchVehicleStatus("REMOVED", `${vehicleName} removed from active inventory.`),
      });
      return;
    }
    if (action === "delete") {
      Modal.confirm({
        title: "Delete vehicle?",
        content: `This will permanently delete ${vehicleName}.`,
        okText: "Delete",
        okButtonProps: { danger: true },
        cancelText: "Cancel",
        centered: true,
        onOk: deleteVehicle,
      });
    }
  };

  const openDrawer = (key) => {
    if (key === "appraisalCreate") {
      if (activeAppraisalProcessReference) {
        navigate(`/appraisals/${activeAppraisalProcessReference}/new`);
        return;
      }
      if (vehicleAppraisalType === "ANNUAL_ASSESSMENT") {
        startAnnualAppraisalProcess();
        return;
      }
      startVehicleAppraisal();
      return;
    }
    setActiveDrawer(key);
  };
  const closeDrawer = () => setActiveDrawer(null);

  useEffect(() => {
    if (!id) {
      setVehicleLoading(false);
      setVehicleError("Vehicle id is missing from the page URL.");
      return undefined;
    }
    let isMounted = true;
    setVehicleLoading(true);
    Promise.allSettled([
      publicMode ? careService.vehicles.getPublic(id) : careService.vehicles.get(id),
    ])
      .then(([
        vehicleResult,
      ]) => {
        if (!isMounted) return;
        const staticVehicle = staticVehicleRecordFor(id);
        if (vehicleResult.status === "rejected" && !staticVehicle) {
          throw vehicleResult.reason;
        }
        const vehicleData = vehicleResult.status === "fulfilled" ? vehicleResult.value : staticVehicle;
        setVehicle(vehicleData || null);
        setAssignedStaff([]);
        setVehicleAppraisalProcesses([]);
        setVehicleDailyNotes([]);
        setVehicleDocuments([]);
        setVehicleNegotiatedCarePlans([]);
        setVehicleContacts([]);
        setVehicleAppraisalRecords([]);
        setVehicleRelatedRecordsError("");
        setVehicleAppraisalsError("");
        setOnboardingPreview(null);
        setOnboardingPreviewError("");
        setVehicleError("");
      })
      .catch((err) => {
        if (!isMounted) return;
        setVehicle(null);
        setAssignedStaff([]);
        setVehicleAppraisalProcesses([]);
        setVehicleAppraisalsError("");
        setVehicleDailyNotes([]);
        setVehicleDocuments([]);
        setVehicleNegotiatedCarePlans([]);
        setVehicleContacts([]);
        setVehicleAppraisalRecords([]);
        setVehicleRelatedRecordsError("");
        setOnboardingPreview(null);
        setOnboardingPreviewError("");
        setVehicleError(err?.response?.data?.message || err?.message || "Unable to load vehicle details.");
      })
      .finally(() => {
        if (isMounted) setVehicleLoading(false);
      });
    return () => {
      isMounted = false;
    };
  }, [id, navigate, publicMode]);

  return (
    <CareShell className="dashboard-content vehicle-content vehicle-hub-page">
      {publicMode ? (
        <Seo
          title={`${vehicleLoading ? "Vehicle Details" : vehicleName} | ${dealerName}`}
          description={vehicleSeoDescription}
          canonicalPath={`/inventory/${id}`}
          favicon={dealerFavicon}
          image={primaryVehiclePhoto || dealerLogo}
          type="product"
          structuredData={vehicleStructuredData}
        />
      ) : null}
      <div className="crumbs">{publicMode ? <Link to="/">Inventory</Link> : "Vehicles"} <i className="ti ti-chevron-right" /> {vehicleName}</div>
      {vehicleLoading ? (
        <div className="auto-vehicle-detail-loading vehicle-page-loading page-inline-loader" role="status" aria-live="polite" aria-label="Loading vehicle details">
          <i className="ti ti-loader-2" aria-hidden="true" />
        </div>
      ) : null}
      {vehicleError ? <p className="form-error">{vehicleError}</p> : null}
      <section className="vehicle-hub-hero">
        <div className="vehicle-hub-identity">
          <VehicleImage
            src={primaryVehiclePhoto}
            alt=""
            className="vehicle-photo vehicle-hub-photo"
            placeholderLabel={vehicleLoading ? "Vehicle photo loading" : `${vehicleInitials} vehicle photo placeholder`}
          />
          <div className="vehicle-hub-title">
            <span className="vehicle-profile-label">Vehicle Profile <em className={vehicleStatusClass}>{vehicleStatus}</em></span>
            <h1>{vehicleLoading ? "Loading vehicle..." : vehicleName}</h1>
            <div className="vehicle-hero-title-actions">
              <em className="vehicle-ncp-status">{vehiclePriceDisplay}</em>
              <em className="vehicle-care-level-status">{vehicleMileageDisplay}</em>
            </div>
          </div>
        </div>
      </section>
      <VehicleListingDetails
        details={vehicleListingDetails}
        managementActionLoading={vehicleActionLoading}
        onAction={openDrawer}
        onInquiry={openVehicleInquiry}
        onManagementAction={handleVehicleManagementAction}
        publicMode={publicMode}
        showMarkActive={showMarkVehicleActive}
        vehicle={vehicle}
      />
      {publicMode ? (
        <Modal
          centered
          className="auto-inquiry-modal"
          footer={null}
          onCancel={closeVehicleInquiry}
          open={inquiryOpen}
          title={`I'm interested in this ${vehicleName}`}
          width={660}
        >
          <form className="auto-inquiry-form" onSubmit={submitVehicleInquiry}>
            <div className="auto-inquiry-vehicle-card">
              <div className="auto-inquiry-vehicle-photo">
                <VehicleImage src={primaryVehiclePhoto} alt="" />
              </div>
              <div>
                <strong>{vehicleName}</strong>
                <span>{vehicleStatus}</span>
              </div>
              <b>{vehiclePrice || "Price pending"}</b>
            </div>
            <p className="auto-inquiry-copy">Confirm availability and pricing for this vehicle.</p>
            <div className="auto-inquiry-grid">
              <label>
                <span>First name</span>
                <input
                  autoComplete="given-name"
                  onChange={(event) => updateInquiryField("firstName", event.target.value)}
                  placeholder="First name"
                  required
                  value={inquiryForm.firstName}
                />
              </label>
              <label>
                <span>Last name</span>
                <input
                  autoComplete="family-name"
                  onChange={(event) => updateInquiryField("lastName", event.target.value)}
                  placeholder="Last name"
                  required
                  value={inquiryForm.lastName}
                />
              </label>
              <label className="auto-inquiry-wide">
                <span>Email address</span>
                <input
                  autoComplete="email"
                  onChange={(event) => updateInquiryField("email", event.target.value)}
                  placeholder="Email address"
                  required
                  type="email"
                  value={inquiryForm.email}
                />
              </label>
              <label>
                <span>ZIP code</span>
                <input
                  autoComplete="postal-code"
                  inputMode="numeric"
                  onChange={(event) => updateInquiryField("zipCode", event.target.value)}
                  placeholder="98012"
                  required
                  value={inquiryForm.zipCode}
                />
              </label>
              <label>
                <span>Phone (optional)</span>
                <input
                  autoComplete="tel"
                  onChange={(event) => updateInquiryField("phone", event.target.value)}
                  placeholder="+1 555-555-5555"
                  type="tel"
                  value={inquiryForm.phone}
                />
              </label>
              <div className="auto-inquiry-wide auto-inquiry-schedule">
                <div>
                  <strong>Schedule a visit?</strong>
                </div>
                <div className="auto-inquiry-schedule-options" role="group" aria-label="Schedule a visit?">
                  <button
                    className={inquiryForm.scheduleVisit === "yes" ? "active" : ""}
                    type="button"
                    onClick={() => updateInquiryField("scheduleVisit", "yes")}
                  >
                    <i className="ti ti-circle-check" /> Yes
                  </button>
                  <button
                    className={inquiryForm.scheduleVisit === "no" ? "active" : ""}
                    type="button"
                    onClick={() => setInquiryForm((current) => ({ ...current, scheduleVisit: "no", visitDate: "", visitTime: "" }))}
                  >
                    <i className="ti ti-circle-x" /> No
                  </button>
                </div>
              </div>
              {inquiryForm.scheduleVisit === "yes" ? (
                <>
                  <p className="auto-inquiry-wide auto-inquiry-schedule-help">Pick a date and time if you want the seller to plan around your visit.</p>
                  <label>
                    <span>Visit date</span>
                    <input
                      onChange={(event) => updateInquiryField("visitDate", event.target.value)}
                      required={inquiryForm.scheduleVisit === "yes"}
                      type="date"
                      value={inquiryForm.visitDate}
                    />
                  </label>
                  <label>
                    <span>Visit time</span>
                    <input
                      onChange={(event) => updateInquiryField("visitTime", event.target.value)}
                      required={inquiryForm.scheduleVisit === "yes"}
                      step="60"
                      type="time"
                      value={inquiryForm.visitTime}
                    />
                  </label>
                </>
              ) : null}
              <label className="auto-inquiry-wide">
                <span>Message to seller (optional)</span>
                <textarea
                  onChange={(event) => updateInquiryField("messageToSeller", event.target.value)}
                  placeholder="Add any questions or details for the seller"
                  value={inquiryForm.messageToSeller}
                />
              </label>
            </div>
            {inquiryError ? <p className="auto-inquiry-error">{inquiryError}</p> : null}
            <p className="auto-inquiry-disclaimer">
              By clicking Check availability, you agree to send this information to {dealerName} so they can respond about this vehicle.
            </p>
            <div className="auto-inquiry-actions">
              <button className="btn primary" disabled={inquirySubmitting} type="submit">
                <i className="ti ti-send" /> {inquirySubmitting ? "Sending..." : "Check availability"}
              </button>
            </div>
          </form>
        </Modal>
      ) : null}
      <Drawer
        title={drawer?.title}
        placement="right"
        open={Boolean(drawer)}
        onClose={closeDrawer}
        width={520}
        className="vehicle-action-drawer"
      >
        {drawer ? (
          <div className="vehicle-drawer-content">
            <p>{drawer.intro}</p>
            {drawer.form === "appraisal" ? (
              <AppraisalDrawerForm />
            ) : drawer.sections ? (
              <div className="vehicle-drawer-list persona-drawer-list">
                {drawer.sections.map((section) => (
                  <article key={section.title}>
                    <strong>{section.title}</strong>
                    <dl>
                      {section.rows.map(([label, value]) => (
                        <div key={label}>
                          <dt>{label}</dt>
                          <dd>{value}</dd>
                        </div>
                      ))}
                    </dl>
                  </article>
                ))}
              </div>
            ) : (
              <div className="vehicle-drawer-list">
                {drawer.rows.map(([label, text]) => (
                  <article key={label}>
                    <strong>{label}</strong>
                    <span>{text}</span>
                  </article>
                ))}
              </div>
            )}
            {drawer.contactActions ? (
              <div className="vehicle-contact-actions">
                {drawer.contactActions.map(([label, icon, href]) => (
                  <a className="mini-btn" href={href} key={label}><i className={`ti ${icon}`} /> {label}</a>
                ))}
              </div>
            ) : null}
            {!drawer.form ? (
              <button className="btn primary" type="button">{drawer.action}</button>
            ) : null}
          </div>
        ) : null}
      </Drawer>
    </CareShell>
  );
}
